import { Injectable, Injector, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of, forkJoin } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as moment from 'moment';
import { CommonService } from 'src/app/shared/services/common.service';
import { afterRfiCreated } from './rfi-create-outcome';
import { SheetMarkupQueueService } from 'src/app/shared/services/sheet-markup-queue.service';
import { AnalyticsService } from 'src/app/shared/services/analytics.service';
import { WorkflowService } from 'src/app/shared/services/workflow.service';
import * as fromRoot from '../../../state/app.state';
import {
  AcceptExternalTestLabScheduleFailure,
  AcceptExternalTestLabScheduleRequest,
  AcceptExternalTestLabScheduleSuccess,
  CancelExternalTestFailure,
  CancelExternalTestRequest,
  CancelExternalTestSuccess,
  AddContractorStatusFailure,
  AddContractorStatusRequest,
  AddContractorStatusSuccess,
  AddDocumentFailure,
  AddDocumentRequest,
  AddDocumentSuccess,
  AddIssuesDetailsFailure,
  AddIssuesDetailsRequest,
  AddIssuesDetailsSuccess,
  ChangeIssueStatusFailure,
  ChangeIssueStatusRequest,
  ChangeIssueStatusSuccess,
  CheckIfDailyReportCanBeCreatedFailure,
  CheckIfDailyReportCanBeCreatedRequest,
  CheckIfDailyReportCanBeCreatedSuccess,
  CopyDailyReportFailure,
  CopyDailyReportRequest,
  CopyDailyReportSuccess,
  CreateActivityTypeLookupFailure,
  CreateActivityTypeLookupRequest,
  CreateActivityTypeLookupSuccess,
  CreateDailyReportDraftFailure,
  CreateDailyReportDraftRequest,
  CreateDailyReportDraftSuccess,
  CreateDailyReportFailure,
  CreateDailyReportRequest,
  CreateDailyReportSuccess,
  CreateEquipmentLookupFailure,
  CreateEquipmentLookupRequest,
  CreateEquipmentLookupSuccess,
  CreateFolderFailure,
  CreateFolderRequest,
  CreateFolderSuccess,
  CreateLaborLookupFailure,
  CreateLaborLookupRequest,
  CreateLaborLookupSuccess,
  CreateProjectInvitationFailure,
  CreateProjectInvitationRequest,
  CreateProjectInvitationSuccess,
  CreateRFIFailure,
  CreateRFIRequest,
  CreateRFISuccess,
  CreateSubmittalsItemFailure,
  CreateSubmittalsItemRequest,
  CreateSubmittalsItemSuccess,
  CreateSubmittalsRegistryItemFailure,
  CreateSubmittalsRegistryItemRequest,
  CreateSubmittalsRegistryItemSuccess,
  DeleteActivityIdsDetailsFailure,
  DeleteActivityIdsDetailsRequest,
  DeleteActivityIdsDetailsSuccess,
  DeleteSubmittalsItemFailure,
  DeleteSubmittalsItemRequest,
  DeleteSubmittalsItemSuccess,
  DeleteActivityTypeLookupFailure,
  DeleteActivityTypeLookupRequest,
  DeleteActivityTypeLookupSuccess,
  DeleteCIWRFailure,
  DeleteCIWRRequest,
  DeleteCIWRSuccess,
  DeleteDailyReportFailure,
  DeleteDailyReportRequest,
  DeleteDailyReportSuccess,
  DeleteDocumentFailure,
  DeleteDocumentRequest,
  DeleteDocumentSuccess,
  DeleteEquipmentLookupFailure,
  DeleteEquipmentLookupRequest,
  DeleteEquipmentLookupSuccess,
  DeleteFolderFailure,
  DeleteFolderRequest,
  DeleteFolderSuccess,
  DeleteLaborLookupFailure,
  DeleteLaborLookupRequest,
  DeleteLaborLookupSuccess,
  DeletePayappFailure,
  DeletePayappRequest,
  DeletePayappSuccess,
  DeleteVideoFromMuxDbFailure,
  DeleteVideoFromMuxDbRequest,
  DeleteVideoFromMuxDbSuccess,
  DownloadReportFailure,
  DownloadReportRequest,
  DownloadReportSuccess,
  ExecuteCurrentActionStateFailure,
  ExecuteCurrentActionStateRequest,
  ExecuteCurrentActionStateSuccess,
  ExecuteSubmitRFIWorkFlowFailure,
  ExecuteSubmitRFIWorkFlowRequest,
  ExecuteSubmitRFIWorkFlowSuccess,
  ExecuteSubmitWorkFlowFailure,
  ExecuteSubmitWorkFlowRequest,
  ExecuteSubmitWorkFlowSuccess,
  ExecuteWorkflowActionStateFailure,
  ExecuteWorkflowActionStateRequest,
  ExecuteWorkflowActionStateSuccess,
  FetchActivityIdsListFailure,
  FetchActivityIdsListRequest,
  FetchActivityIdsListSuccess,
  FetchActivityTypeLookupFailure,
  FetchActivityTypeLookupRequest,
  FetchActivityTypeLookupSuccess,
  FetchExternalLabLookupFailure,
  FetchExternalLabLookupRequest,
  FetchExternalLabLookupSuccess,
  FetchMaterialTestLabListFailure,
  FetchMaterialTestLabListRequest,
  FetchMaterialTestLabListSuccess,
  CreateExternalLabFailure,
  CreateExternalLabRequest,
  CreateExternalLabSuccess,
  UpdateExternalLabFailure,
  UpdateExternalLabRequest,
  UpdateExternalLabSuccess,
  DeleteExternalLabLookupFailure,
  DeleteExternalLabLookupRequest,
  DeleteExternalLabLookupSuccess,
  CreateProjectMaterialTestFailure,
  CreateProjectMaterialTestRequest,
  CreateProjectMaterialTestSuccess,
  DeleteProjectMaterialTestFailure,
  DeleteProjectMaterialTestRequest,
  DeleteProjectMaterialTestSuccess,
  UpdateProjectMaterialTestFailure,
  UpdateProjectMaterialTestRequest,
  UpdateProjectMaterialTestSuccess,
  FetchAllBidItemsFailure,
  FetchAllBidItemsRequest,
  FetchAllBidItemsSuccess,
  FetchBasisOfPayOptionsFailure,
  FetchBasisOfPayOptionsRequest,
  FetchBasisOfPayOptionsSuccess,
  FetchBidItemListWithChangeOrderFailure,
  FetchBidItemListWithChangeOrderInSideNavFailure,
  FetchBidItemListWithChangeOrderInSideNavRequest,
  FetchBidItemListWithChangeOrderInSideNavSuccess,
  FetchBidItemListWithChangeOrderRequest,
  FetchBidItemListWithChangeOrderSuccess,
  FetchBidItemsListFailure,
  FetchBidItemsListRequest,
  FetchBidItemsListSuccess,
  FetchBidItemsSitesListFailure,
  FetchBidItemsSitesListRequest,
  FetchBidItemsSitesListSuccess,
  FetchBidSheetFailure,
  FetchBidSheetRequest,
  FetchBidSheetSuccess,
  FetchSettingsBidSheetFailure,
  FetchSettingsBidSheetRequest,
  FetchSettingsBidSheetSuccess,
  FetchSettingsBidItemsFailure,
  FetchSettingsBidItemsRequest,
  FetchSettingsBidItemsSuccess,
  FetchSettingsBidItemDetailsFailure,
  FetchSettingsBidItemDetailsRequest,
  FetchSettingsBidItemDetailsSuccess,
  FetchBidItemFieldsFailure,
  FetchBidItemFieldsRequest,
  FetchBidItemFieldsSuccess,
  SettingsCreateBidItemFieldFailure,
  SettingsCreateBidItemFieldRequest,
  SettingsCreateBidItemFieldSuccess,
  SettingsUpdateBidItemFieldFailure,
  SettingsUpdateBidItemFieldRequest,
  SettingsUpdateBidItemFieldSuccess,
  FetchSettingsProjectMaterialsListFailure,
  FetchSettingsProjectMaterialsListRequest,
  FetchSettingsProjectMaterialsListSuccess,
  SettingsAddMaterialToBidItemFailure,
  SettingsAddMaterialToBidItemRequest,
  SettingsAddMaterialToBidItemSuccess,
  SettingsRemoveMaterialFromBidItemFailure,
  SettingsRemoveMaterialFromBidItemRequest,
  SettingsRemoveMaterialFromBidItemSuccess,
  SettingsUpdateBidItemSiteQuantityFailure,
  SettingsUpdateBidItemSiteQuantityRequest,
  SettingsUpdateBidItemSiteQuantitySuccess,
  SettingsCreateBidItemRequest,
  SettingsCreateBidItemSuccess,
  SettingsCreateBidItemFailure,
  SettingsUpdateBidItemRequest,
  SettingsUpdateBidItemSuccess,
  SettingsUpdateBidItemFailure,
  SettingsDeleteBidItemRequest,
  SettingsDeleteBidItemSuccess,
  SettingsDeleteBidItemFailure,
  FetchChangeInWorkCodeOptionsFailure,
  FetchChangeInWorkCodeOptionsRequest,
  FetchChangeInWorkCodeOptionsSuccess,
  FetchChangeInWorkRequestListFailure,
  FetchChangeInWorkRequestListRequest,
  FetchChangeInWorkRequestListSuccess,
  FetchChangeInWorkRequestWithIdFailure,
  FetchChangeInWorkRequestWithIdRequest,
  FetchChangeInWorkRequestWithIdSuccess,
  FetchChangeOrderFilterFailure,
  FetchChangeOrderFilterInSideNavFailure,
  FetchChangeOrderFilterInSideNavRequest,
  FetchChangeOrderFilterInSideNavSuccess,
  FetchChangeOrderFilterRequest,
  FetchChangeOrderFilterSuccess,
  FetchChangeOrdersRequestListFailure,
  FetchChangeOrdersRequestListRequest,
  FetchChangeOrdersRequestListSuccess,
  FetchChangeOrdersRequestWithIdFailure,
  FetchChangeOrdersRequestWithIdRequest,
  FetchChangeOrdersRequestWithIdSuccess,
  CreateChangeOrderFromRequestsFailure,
  CreateChangeOrderFromRequestsRequest,
  CreateChangeOrderFromRequestsSuccess,
  FetchAvailableCorsFailure,
  FetchAvailableCorsRequest,
  FetchAvailableCorsSuccess,
  LinkRequestsToChangeOrderFailure,
  LinkRequestsToChangeOrderRequest,
  LinkRequestsToChangeOrderSuccess,
  UnlinkRequestFromChangeOrderFailure,
  UnlinkRequestFromChangeOrderRequest,
  UnlinkRequestFromChangeOrderSuccess,
  FetchDailyReportFailure,
  FetchDailyReportLineageFailure,
  FetchDailyReportLineageRequest,
  FetchDailyReportLineageSuccess,
  FetchDailyReportRequest,
  FetchDailyReportsByDateFailure,
  FetchDailyReportsByDateRequest,
  FetchDailyReportsByDateSuccess,
  FetchDailyReportSuccess,
  FetchEquipmentFilterFailure,
  FetchEquipmentFilterRequest,
  FetchEquipmentFilterSuccess,
  FetchEquipmentLookupFailure,
  FetchEquipmentLookupRequest,
  FetchEquipmentLookupSuccess,
  FetchExternalTestsFailure,
  FetchExternalTestsRequest,
  FetchExternalTestsSuccess,
  FetchIssuesFailure,
  FetchIssuesRequest,
  FetchIssuesSuccess,
  FetchIssueTypesFailure,
  FetchIssueTypesRequest,
  FetchIssueTypesSuccess,
  FetchLabNameListFailure,
  FetchLabNameListRequest,
  FetchLabNameListSuccess,
  FetchLaborFilterFailure,
  FetchLaborFilterRequest,
  FetchLaborFilterSuccess,
  FetchLaborLookupFailure,
  FetchLaborLookupRequest,
  FetchLaborLookupSuccess,
  FetchLineageFailure,
  FetchLineageRequest,
  FetchLineageSuccess,
  FetchMaterialTestNameListFailure,
  FetchMaterialTestNameListRequest,
  FetchMaterialTestNameListSuccess,
  FetchProjectMaterialTestListFailure,
  FetchProjectMaterialTestListRequest,
  FetchProjectMaterialTestListSuccess,
  FetchPayappDetailsFailure,
  FetchPayappDetailsRequest,
  FetchPayappDetailsSuccess,
  FetchPayappItemsFailure,
  FetchPayappItemsRequest,
  FetchPayappItemsSuccess,
  FetchPayappsFailure,
  FetchPayappsRequest,
  FetchPayappsSuccess,
  FetchProjectAccountsFailure,
  FetchProjectAccountsRequest,
  FetchProjectAccountsSuccess,
  FetchProjectDetailsFailure,
  FetchProjectDetailsRequest,
  FetchProjectDetailsSuccess,
  UpdateProjectFailure,
  UpdateProjectRequest,
  UpdateProjectSuccess,
  FetchProjectDocumentsFailure,
  FetchProjectDocumentsRequest,
  FetchProjectDocumentsSuccess,
  FetchProjectInvitationsFailure,
  FetchProjectInvitationsRequest,
  FetchProjectInvitationsSuccess,
  FetchProjectMembersFailure,
  FetchProjectMembersListFailure,
  FetchProjectMembersListRequest,
  FetchProjectMembersListSuccess,
  FetchContractorsListFailure,
  FetchContractorsListRequest,
  FetchContractorsListSuccess,
  FetchSitesListRequest,
  FetchSitesListSuccess,
  FetchSitesListFailure,
  DeleteSiteRequest,
  DeleteSiteSuccess,
  DeleteSiteFailure,
  CreateSiteRequest,
  CreateSiteSuccess,
  CreateSiteFailure,
  UpdateSiteRequest,
  UpdateSiteSuccess,
  UpdateSiteFailure,
  CreateContractorFailure,
  CreateContractorRequest,
  CreateContractorSuccess,
  UpdateContractorFailure,
  UpdateContractorRequest,
  UpdateContractorSuccess,
  DeleteContractorFailure,
  DeleteContractorRequest,
  DeleteContractorSuccess,
  FetchDeliveryTrackingFieldsFailure,
  FetchDeliveryTrackingFieldsRequest,
  FetchDeliveryTrackingFieldsSuccess,
  CreateDeliveryTrackingFieldFailure,
  CreateDeliveryTrackingFieldRequest,
  CreateDeliveryTrackingFieldSuccess,
  UpdateDeliveryTrackingFieldFailure,
  UpdateDeliveryTrackingFieldRequest,
  UpdateDeliveryTrackingFieldSuccess,
  DeleteDeliveryTrackingFieldFailure,
  DeleteDeliveryTrackingFieldRequest,
  DeleteDeliveryTrackingFieldSuccess,
  FetchExistingMembersListFailure,
  FetchExistingMembersListRequest,
  FetchExistingMembersListSuccess,
  InviteUserFailure,
  InviteUserRequest,
  InviteUserSuccess,
  UpdateUserFailure,
  UpdateUserRequest,
  UpdateUserSuccess,
  DeleteUserFailure,
  DeleteUserRequest,
  DeleteUserSuccess,
  CheckEmailExistsRequest,
  CheckEmailExistsSuccess,
  CheckEmailExistsFailure,
  CheckPhoneExistsRequest,
  CheckPhoneExistsSuccess,
  CheckPhoneExistsFailure,
  FetchProjectMembersRequest,
  FetchProjectMembersSuccess,
  FetchProjectReportFailure,
  FetchProjectReportRequest,
  FetchProjectReportsListFailure,
  FetchProjectReportsListRequest,
  FetchProjectReportsListSuccess,
  FetchProjectReportSuccess,
  FetchProjectRolesListFailure,
  FetchProjectRolesListRequest,
  FetchProjectRolesListSuccess,
  FetchProjectSitesForInvitationFailure,
  FetchProjectSitesForInvitationRequest,
  FetchProjectSitesForInvitationSuccess,
  FetchProjectsListFailure,
  FetchProjectsListRequest,
  FetchProjectsListSuccess,
  FetchReportFailure,
  FetchReportRequest,
  FetchReportSuccess,
  FetchRFIItemDetailsWithIdFailure,
  FetchRFIItemDetailsWithIdRequest,
  FetchRFIItemDetailsWithIdSuccess,
  FetchRFINoFailure,
  FetchRFINoRequest,
  FetchRFINoSuccess,
  FetchRFIsListFailure,
  FetchRFIsListRequest,
  FetchRFIsListSuccess,
  FetchSiteDetailsFailure,
  FetchSiteDetailsRequest,
  FetchSiteDetailsSuccess,
  FetchSiteWiseDailyReportSummaryFailure,
  FetchSiteWiseDailyReportSummaryRequest,
  FetchSiteWiseDailyReportSummarySuccess,
  FetchSubContractorFilterFailure,
  FetchSubContractorFilterRequest,
  FetchSubContractorFilterSuccess,
  FetchSubmittalsItemDetailsWithIdFailure,
  FetchSubmittalsItemDetailsWithIdRequest,
  FetchSubmittalsItemDetailsWithIdSuccess,
  FetchSubmittalsItemsListFailure,
  FetchSubmittalsItemsListRequest,
  FetchSubmittalsItemsListSuccess,
  FetchSubmittalsRegistryListFailure,
  FetchSubmittalsRegistryListRequest,
  FetchSubmittalsRegistryListSuccess,
  FetchUsersListFailure,
  FetchUsersListRequest,
  FetchUsersListSuccess,
  GeneratePayappFailure,
  GeneratePayappRequest,
  GeneratePayappSuccess,
  RecordExternalTestLabTestDetailsFailure,
  RecordExternalTestLabTestDetailsRequest,
  RecordExternalTestLabTestDetailsSuccess,
  SaveChangeInWorkRequestFailure,
  SaveChangeInWorkRequestRequest,
  SaveChangeInWorkRequestSuccess,
  SaveChangeOrdersRequestFailure,
  SaveChangeOrdersRequestRequest,
  SaveChangeOrdersRequestSuccess,
  SendChangeInWorkRequestReminderFailure,
  SendChangeInWorkRequestReminderRequest,
  SendChangeInWorkRequestReminderSuccess,
  SendChangeOrdersRequestReminderFailure,
  SendChangeOrdersRequestReminderRequest,
  SendChangeOrdersRequestReminderSuccess,
  SaveDailyReportFailure,
  SaveDailyReportRequest,
  SaveDailyReportSuccess,
  SaveExternalTestFailure,
  SaveExternalTestRequest,
  SaveExternalTestSuccess,
  SendExternalTestToLabFailure,
  SendExternalTestToLabRequest,
  SendExternalTestToLabSuccess,
  SubmitReviewersFailure,
  SubmitReviewersRequest,
  SubmitReviewersSuccess,
  SubmitRfiReviewersFailure,
  SubmitRfiReviewersRequest,
  SubmitRfiReviewersSuccess,
  UpdateActivityIdsDetailsFailure,
  UpdateActivityIdsDetailsRequest,
  UpdateActivityIdsDetailsSuccess,
  UpdateDocumentNameFailure,
  UpdateDocumentNameRequest,
  UpdateDocumentNameSuccess,
  UpdateExternalTestLabTestReportFailure,
  UpdateExternalTestLabTestReportRequest,
  UpdateExternalTestLabTestReportSuccess,
  UpdateExternalTestLabTestResultFailure,
  UpdateExternalTestLabTestResultRequest,
  UpdateExternalTestLabTestResultSuccess,
  FetchBillingSettingsRequest,
  FetchBillingSettingsSuccess,
  FetchBillingSettingsFailure,
  UpdateBillingSettingsRequest,
  UpdateBillingSettingsSuccess,
  UpdateBillingSettingsFailure,
  FetchProjectCalendarRequest,
  FetchProjectCalendarSuccess,
  FetchProjectCalendarFailure,
  UpdateProjectCalendarRequest,
  UpdateProjectCalendarSuccess,
  UpdateProjectCalendarFailure,
  InitializeProjectCalendarRequest,
  InitializeProjectCalendarSuccess,
  InitializeProjectCalendarFailure,
  FetchPayAppSummaryRequest,
  FetchPayAppSummarySuccess,
  FetchPayAppSummaryFailure,
  FetchPayAppTemplatesRequest,
  FetchPayAppTemplatesSuccess,
  FetchPayAppTemplatesFailure,
  CreatePayAppTemplateRequest,
  CreatePayAppTemplateSuccess,
  CreatePayAppTemplateFailure,
  UpdatePayAppTemplateRequest,
  UpdatePayAppTemplateSuccess,
  UpdatePayAppTemplateFailure,
  DeletePayAppTemplateRequest,
  DeletePayAppTemplateSuccess,
  DeletePayAppTemplateFailure,
  PreviewPayAppTemplateRequest,
  PreviewPayAppTemplateSuccess,
  PreviewPayAppTemplateFailure,
  UpdateFolderNameFailure,
  UpdateFolderNameRequest,
  UpdateFolderNameSuccess,
  UpdateIssuesDetailsFailure,
  UpdateIssuesDetailsRequest,
  UpdateIssuesDetailsSuccess,
  UpdatePayappStatusFailure,
  UpdatePayappStatusRequest,
  UpdatePayappStatusSuccess,
  UploadActivityIdsFileFailure,
  UploadActivityIdsFileRequest,
  UploadActivityIdsFileSuccess,
  UploadChangeOrderFileFailure,
  UploadChangeOrderFileRequest,
  UploadChangeOrderFileSuccess,
  UploadSettingsBidSheetCsvFailure,
  UploadSettingsBidSheetCsvRequest,
  UploadSettingsBidSheetCsvSuccess,
  UploadSubmittalLogFileFailure,
  UploadSubmittalLogFileRequest,
  UploadSubmittalLogFileSuccess,
  ScanBatchTicketRequest,
  ScanBatchTicketSuccess,
  ScanBatchTicketFailure,
  FetchWarrantiesRequest,
  FetchWarrantiesSuccess,
  FetchWarrantiesFailure,
  CreateWarrantyRequest,
  CreateWarrantySuccess,
  CreateWarrantyFailure,
  UpdateWarrantyRequest,
  UpdateWarrantySuccess,
  UpdateWarrantyFailure,
  DeleteWarrantyRequest,
  DeleteWarrantySuccess,
  DeleteWarrantyFailure,
  FetchWarrantyClaimsRequest,
  FetchWarrantyClaimsSuccess,
  FetchWarrantyClaimsFailure,
  CreateWarrantyClaimRequest,
  CreateWarrantyClaimSuccess,
  CreateWarrantyClaimFailure,
  FetchWarrantyClaimDetailRequest,
  FetchWarrantyClaimDetailSuccess,
  FetchWarrantyClaimDetailFailure,
  WarrantyClaimActionRequest,
  WarrantyClaimActionSuccess,
  WarrantyClaimActionFailure,
} from './projects.actions';
import { ProjectsService } from './projects.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../shared/services';
import { DailyReportService } from '../pages/site-details-page/tab-screens/daily-report/daily-report.service';
import { SiteMaterialTestService } from '../../project/material-test/services/site-material-test.service';
import { DatePipe } from '@angular/common';
import { dailyReport, projectDetails } from './projects.selectors';
import { IssueService } from '../../issue/issue.service';
import { materialTestStatusMap } from '../../../shared/constants/commonConstants';
import { Parser } from 'expr-eval';
import { FetchTranslationsRequest } from '../../../shared/core/shared.actions';
import { SearchService } from '../../search/core/search.service';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from '../../../shared/components/alert-dialog/alert-dialog.component';
import { ChangeInWorkRequestReportComponent } from '../pages/projects-details-page/components/tab-screens/change-in-work-request/change-in-work-request-report/change-in-work-request-report.component';
import { ChangeOrdersRequestReportComponent } from '../pages/projects-details-page/components/tab-screens/change-orders-request/change-orders-request-report/change-orders-request-report.component';
import { saveAs } from 'file-saver';
import { openReportChangedDialog, ReportChangedBody, reportChangedBody } from '../../../shared/helpers/report-conflict';

@Injectable()
export class ProjectsEffects {
  fetchProjectsList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectsListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectsList(payload).pipe(
          map(response => {
            const { data } = response;

            const projectsFilterList = [];
            const sitesFilterData = {};

            data?.map(d => {
              d['durationProgress'] = (100 * d.duration_used) / d.duration;
              d['valueProgress'] = (100 * d.earned_value) / d.project_value;

              projectsFilterList.push({ value: d.id, label: d.name });

              const sitesFilterList = [];
              d.sites?.map(s => {
                sitesFilterList.push({ value: s.id, label: s.name });
              });

              sitesFilterData[d.id] = sitesFilterList;
            });

            return FetchProjectsListSuccess({
              payload: {
                projectsList: data,
                projectsFilterList,
                sitesFilterData,
              },
            });
          }),
          catchError(() => {
            return of(FetchProjectsListFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchProjectsListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchProjectsListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchProjectDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectDetailsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectDetails(payload).pipe(
          map(response => {
            const { data } = response;
            data['durationProgress'] = (100 * data.duration_used) / data.duration;
            data['valueProgress'] = (100 * data.earned_value) / data.project_value;
            data['internal_tests'] =
              data?.['internal_tests']?.map(test => {
                return {
                  ...test,
                  value: test.id,
                  label: test.name,
                };
              }) || [];

            data.internal_tests.forEach(test => {
              test.fields?.map((field: any) => {
                if (field.range_values && Object.keys(field.range_values)?.length) {
                  let hintText = '';
                  const { min, max } = field.range_values;

                  if (min !== null && max !== null) {
                    hintText = `Range: min ${min} - max ${max}`;
                  } else if (min !== null) {
                    hintText = `Range min: ${min}`;
                  } else if (max !== null) {
                    hintText = `Range max: ${max}`;
                  }
                  field['hintText'] = hintText;
                }
              });
            });

            data['activity_codes'] = data['activity_codes']?.map(activityCode => {
              return {
                ...activityCode,
                segmentLocation: `${activityCode?.segment} - ${activityCode?.location}`,
              };
            });

            data['activityCodesList'] = data['activity_codes']?.map(activityCode => {
              return {
                ...activityCode,
                value: activityCode?.id,
                label: `${activityCode?.activity_id} - ${activityCode?.name}`,
              };
            });

            data['activityCodesLocationList'] = Object.values(
              data['activity_codes']?.reduce((acc, item) => {
                acc[item?.location] = { label: item?.location, value: item?.segmentLocation, segment: item?.segment };
                return acc;
              }, {}) || {}
            );

            data['activityCodesSegmentList'] = Object.values(
              data['activity_codes']?.reduce((acc, item) => {
                acc[item.segment] = { label: item.segment, value: item.segment };
                return acc;
              }, {}) || {}
            );

            data['control_forms'] = data['control_forms']?.map(controlForms => {
              return {
                ...controlForms,
                label: controlForms.name,
                value: controlForms.id,
              };
            });

            data['activity_types'] = data['activity_types']?.map(activityType => {
              return {
                ...activityType,
                label: activityType.value,
              };
            });

            data.control_forms?.forEach(o => {
              // Process sections
              if (o?.ui_config?.sections?.length > 0) {
                o.ui_config.sections = o?.ui_config?.sections?.map(section => ({
                  ...section,
                  label: section?.description,
                  value: section?.name,
                }));
              }

              // Helper to process fields array
              const processFields = fields => {
                return fields.map(field => {
                  let componentType: any;

                  if (field?.pick_list || field?.ui_config?.pick_list_values?.length || field?.multi_select) {
                    componentType = 'dropdown';
                  } else {
                    componentType = field?.type;
                  }

                  if (Array.isArray(field?.ui_config?.pick_list_values)) {
                    field.ui_config.pick_list_values = field?.ui_config?.pick_list_values?.map(plv => ({
                      label: plv,
                      value: plv,
                    }));
                  }

                  return {
                    ...field,
                    componentType,
                  };
                });
              };

              // Apply to all three places
              if (o?.ui_config?.header?.fields?.length > 0) {
                o.ui_config.header.fields = processFields(o.ui_config.header.fields);
              }
              if (o?.ui_config?.footer?.fields?.length > 0) {
                o.ui_config.footer.fields = processFields(o.ui_config.footer.fields);
              }
              if (o?.ui_config?.lines?.fields?.length > 0) {
                o.ui_config.lines.fields = processFields(o.ui_config.lines.fields);
              }
            });

            data['gauge_numbers'] =
              data?.['gauge_numbers']?.map(gn => {
                return {
                  ...gn,
                  label: gn.value,
                };
              }) || [];

            data['material_sources'] =
              data?.['material_sources']?.map(ms => {
                return {
                  ...ms,
                  label: ms.value,
                };
              }) || [];

            data['material_types'] =
              data?.['material_types']?.map(mt => {
                return {
                  ...mt,
                  label: mt.value,
                };
              }) || [];

            data['activity_codes'] =
              data?.['activity_codes']?.map(ac => {
                return {
                  ...ac,
                  value: ac.id,
                  label: ac.name,
                };
              }) || [];

            data['activity_codes_forms'] =
              data?.['activity_codes']?.map(ac => {
                return {
                  ...ac,
                  value: ac.activity_id,
                  label: `${ac?.activity_id} - ${ac?.name}`,
                };
              }) || [];

            data['sub_contractors'] =
              data?.['sub_contractors']?.map(sc => {
                return {
                  ...sc,
                  value: sc.id,
                  label: sc.name,
                  tag: sc.prime ? 'Prime' : null,
                  employees: sc.employees?.map(e => ({ ...e, value: e.id, label: `${e.first_name} ${e.last_name}` })) || [],
                  equipment: sc.equipment?.map(e => ({ ...e, value: e.id, label: [e.name, e.description].filter(Boolean).join(' - ') })) || [],
                  personnel: sc.personnel?.map(p => ({ ...p, value: p.id, label: p.name })) || [],
                };
              }) || [];

            // Build permitted_contractors on each bid sheet item
            if (data.bidSheet?.bidItems?.length) {
              const primeContractors = (data.sub_contractors || [])
                .filter(sc => sc.prime)
                .map(sc => ({ ...sc, value: sc.id, label: sc.name }));

              const primeIds = primeContractors.map(sc => sc.value);

              data.bidSheet.bidItems = data.bidSheet.bidItems.map(bidItem => {
                const bid_item_contractors = (bidItem.bid_item_sub_contractor || [])
                  .filter(bsc => bsc.sub_contractor?.id && !primeIds.includes(bsc.sub_contractor.id))
                  .map(bsc => ({ ...bsc.sub_contractor, value: bsc.sub_contractor.id, label: bsc.sub_contractor.name }));

                return {
                  ...bidItem,
                  all_permitted_contractors: [...primeContractors, ...bid_item_contractors],
                  bid_item_contractors,
                };
              });
            }

            return FetchProjectDetailsSuccess({
              projectDetails: data,
            });
          }),
          catchError(() => {
            return of(FetchProjectDetailsFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchProjectDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.store.dispatch(
                FetchTranslationsRequest({
                  payload,
                })
              );
            } else if (action.type === FetchProjectDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchProjectDocuments: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectDocumentsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectDocuments(payload).pipe(
          map(response => {
            const { data } = response;
            const rootData = data || response;
            const root = rootData.data || rootData;

            // Helper function to determine file type from MIME
            const getFileType = (mime: string): string => {
              if (mime.startsWith('image/')) {
                return 'picture';
              }
              if (mime.startsWith('video/')) {
                return 'video';
              }
              if (mime.startsWith('application/') || mime.startsWith('text/')) {
                return 'document';
              }
              return 'document';
            };

            // Helper function to format date
            const formatDate = (date: string): string => {
              return date ? moment(date).format('h:mm a, MMM D, YYYY') : '';
            };

            // Flatten hierarchical structure into table rows
            const flattenedRows: any[] = [];

            const processItem = (
              item: any,
              type: 'folder' | 'document' | 'attachment',
              level: number,
              parentId?: string
            ): void => {
              let row: any;

              if (type === 'folder') {
                const totalItems = (item.subfolders?.length || 0) + (item.documents?.length || 0);
                row = {
                  id: item.id,
                  name: item.name,
                  type: 'folder',
                  level,
                  parentId,
                  filesVersions: totalItems > 0 ? `${totalItems} file${totalItems !== 1 ? 's' : ''}` : '',
                  lastModified: formatDate(item.updated_at || item.created_at),
                  hasChildren: totalItems > 0,
                  isExpanded: false,
                  originalData: item,
                };
                flattenedRows.push(row);

                // Process children recursively
                if (item.subfolders?.length > 0) {
                  item.subfolders.forEach((subfolder: any) => {
                    processItem(subfolder, 'folder', level + 1, item.id);
                  });
                }
                if (item.documents?.length > 0) {
                  item.documents.forEach((document: any) => {
                    processItem(document, 'document', level + 1, item.id);
                  });
                }
              } else if (type === 'document') {
                const attachmentCount = item.attachments?.length || 0;
                const hasVersions = attachmentCount > 1;
                const isSingleFile = attachmentCount === 1;
                const mime = item.attachments?.[0]?.mime || item.mime || '';
                const fileType = getFileType(mime);

                row = {
                  id: item.id,
                  name: item.name,
                  type: 'document',
                  level,
                  parentId,
                  filesVersions: hasVersions ? `${attachmentCount} version${attachmentCount !== 1 ? 's' : ''}` : '',
                  lastModified: formatDate(item.updated_at || item.created_at),
                  hasChildren: hasVersions, // Only expandable if multiple versions
                  isExpanded: false,
                  thumb_url: item.attachments?.[0]?.thumb_url || item.attachments?.[0]?.preview_url || null,
                  hasVersions,
                  fileType,
                  size: item.attachments?.[0]?.size || null,
                  originalData: item,
                };
                flattenedRows.push(row);

                // Process attachments
                if (item.attachments?.length > 0) {
                  item.attachments.forEach((attachment: any) => {
                    const attachmentMime = attachment.mime || '';
                    processItem(
                      {
                        ...attachment,
                        name: attachment.name || attachment.original_name,
                        fileType: getFileType(attachmentMime),
                        thumb_url: attachment.thumb_url || attachment.preview_url || null,
                      },
                      'attachment',
                      level + 1,
                      item.id
                    );
                  });
                }
              } else if (type === 'attachment') {
                row = {
                  id: item.id,
                  name: item.name || item.original_name,
                  type: 'attachment',
                  level,
                  parentId,
                  filesVersions: '',
                  lastModified: formatDate(item.created_at),
                  hasChildren: false,
                  isExpanded: false,
                  thumb_url: item.thumb_url || item.preview_url || null,
                  fileType: item.fileType || 'document',
                  size: item.size || null,
                  originalData: item,
                };
                flattenedRows.push(row);
              }
            };

            // Process root level items
            if (root.subfolders?.length > 0) {
              root.subfolders.forEach((subfolder: any) => {
                processItem(subfolder, 'folder', 0);
              });
            }

            if (root.documents?.length > 0) {
              root.documents.forEach((document: any) => {
                processItem(document, 'document', 0);
              });
            }

            return FetchProjectDocumentsSuccess({
              documents: {
                ...root,
                flattenedRows,
              },
            });
          }),
          catchError(() => {
            return of(FetchProjectDocumentsFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchProjectDocumentsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchProjectDocumentsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createProjectFolder: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateFolderRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createFolder(payload).pipe(
          map(response => {
            const { data } = response;
            return CreateFolderSuccess({ data });
          }),
          catchError(() => {
            return of(CreateFolderFailure({}));
          }),
          tap((action: any) => {
            if (action.type === CreateFolderSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.dialog.closeAll();
              this.store.dispatch(
                FetchProjectDocumentsRequest({
                  payload: { projectId: payload.projectId },
                })
              );
            } else if (action.type === CreateFolderFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteFolder: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteFolderRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteFolder(payload).pipe(
          map(response => {
            const { data } = response;
            return DeleteFolderSuccess({ data });
          }),
          catchError(() => {
            return of(DeleteFolderFailure({}));
          }),
          tap((action: any) => {
            if (action.type === DeleteFolderSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.dialog.closeAll();
              this.store.dispatch(
                FetchProjectDocumentsRequest({
                  payload: { projectId: payload.projectId },
                })
              );
            } else if (action.type === DeleteFolderFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateFolderName: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateFolderNameRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateFolderName(payload).pipe(
          map(response => {
            const { data } = response;
            return UpdateFolderNameSuccess({ data });
          }),
          catchError(() => {
            return of(UpdateFolderNameFailure({}));
          }),
          tap((action: any) => {
            if (action.type === UpdateFolderNameSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.dialog.closeAll();
              this.store.dispatch(
                FetchProjectDocumentsRequest({
                  payload: { projectId: payload.projectId },
                })
              );
            } else if (action.type === UpdateFolderNameFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateDocumentName: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateDocumentNameRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateDocumentName(payload).pipe(
          map(response => {
            const { data } = response;
            return UpdateDocumentNameSuccess({ data });
          }),
          catchError(() => {
            return of(UpdateDocumentNameFailure({}));
          }),
          tap((action: any) => {
            if (action.type === UpdateDocumentNameSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.dialog.closeAll();
              this.store.dispatch(
                FetchProjectDocumentsRequest({
                  payload: { projectId: payload.projectId },
                })
              );
            } else if (action.type === UpdateDocumentNameFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  addDocument: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(AddDocumentRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.addDocument(payload).pipe(
          map(response => {
            const { data } = response;
            return AddDocumentSuccess({ data });
          }),
          catchError(() => {
            return of(AddDocumentFailure({}));
          }),
          tap((action: any) => {
            if (action.type === AddDocumentSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.dialog.closeAll();
              this.store.dispatch(
                FetchProjectDocumentsRequest({
                  payload: { projectId: payload.projectId },
                })
              );
            } else if (action.type === AddDocumentFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteDocument: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteDocumentRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteDocument(payload).pipe(
          map(response => {
            const { data } = response;
            return DeleteDocumentSuccess({ data });
          }),
          catchError(() => {
            return of(DeleteDocumentFailure({}));
          }),
          tap((action: any) => {
            if (action.type === DeleteDocumentSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.dialog.closeAll();
              this.store.dispatch(
                FetchProjectDocumentsRequest({
                  payload: { projectId: payload.projectId },
                })
              );
            } else if (action.type === DeleteDocumentFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchSiteWiseDailyReportsSummary: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSiteWiseDailyReportSummaryRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchSiteWiseDailyReportSummary(payload).pipe(
          map(response => {
            const { data } = response;

            return FetchSiteWiseDailyReportSummarySuccess({
              siteWiseDailyReportsSummary: data,
            });
          }),
          catchError(() => {
            return of(FetchSiteWiseDailyReportSummaryFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchSiteWiseDailyReportSummarySuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchSiteWiseDailyReportSummaryFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchProjectMembers$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectMembersRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectMembers(payload).pipe(
          map(response => {
            const { data } = response;

            const currentUser = this.authService.getCurrentUser();

            const projectMembersFilterList = data?.map(o => {
              const name = `${o.first_name} ${o.last_name}`;

              return {
                value: o.id,
                label: o.id === currentUser.id ? `Me (${name})` : name,
              };
            });

            const index = projectMembersFilterList?.findIndex(o => o.label.includes('Me ('));

            if (index > -1) {
              const element = projectMembersFilterList.splice(index, 1);
              projectMembersFilterList.unshift(...element);
            }

            return FetchProjectMembersSuccess({ projectMembersFilterList });
          }),
          catchError(() => {
            return of(FetchProjectMembersFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchProjectMembersSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchProjectMembersFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchProjectAccounts$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectAccountsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectAccounts(payload).pipe(
          map(response => {
            const { data, meta } = response;
            return FetchProjectAccountsSuccess({
              projectAccountsInfo: {
                list: data,
                totalRecords: meta?.totalRecords || 0,
              },
            });
          }),
          catchError(() => {
            return of(FetchProjectAccountsFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchProjectAccountsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchProjectAccountsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchProjectInvitations$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectInvitationsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectInvitations(payload).pipe(
          map(response => {
            const { data, meta } = response;
            return FetchProjectInvitationsSuccess({
              projectInvitationsInfo: {
                list: data || [],
                totalRecords: meta?.totalRecords || 0,
              },
            });
          }),
          catchError(() => {
            return of(FetchProjectInvitationsFailure());
          }),
          tap((action: any) => {
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createProjectInvitation$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateProjectInvitationRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload => {
        const { entityType, siteId, projectId, role, body, qp } = payload;
        const request =
          entityType === 'site'
            ? this.projectsService.createSiteInvitation({ siteId, role, body, qp })
            : this.projectsService.createProjectInvitation({ projectId, role, body, qp });

        return request.pipe(
          map(response => {
            this.commonService.notification('Invitation sent successfully', 'success');
            return CreateProjectInvitationSuccess({ invitation: response?.data });
          }),
          catchError(() => {
            this.commonService.notification('Failed to send invitation', 'error');
            return of(CreateProjectInvitationFailure());
          }),
          tap(() => {
            this.commonService.stopLoading();
          })
        );
      })
    )
  );

  fetchProjectSitesForInvitation$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectSitesForInvitationRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectSitesForInvitation(payload).pipe(
          map(response => {
            const { data } = response;
            const transformedData = (data || []).map((site: any) => ({
              ...site,
              label: site?.name || site?.label,
              value: site?.id || site?.value,
            }));
            return FetchProjectSitesForInvitationSuccess({
              projectSitesForInvitation: transformedData,
            });
          }),
          catchError(() => {
            return of(FetchProjectSitesForInvitationFailure());
          })
        )
      )
    )
  );

  createDailyReport$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateDailyReportRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createDailyReport(payload).pipe(
          map(response => {
            return CreateDailyReportSuccess({ data: response.data });
          }),
          catchError(() => {
            return of(CreateDailyReportFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateDailyReportSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.closeCreateDailyReportDialog.next();
              this.commonService.notification('Daily report created successfully', 'success');
              const { data } = action;
              const url = `/projects/${data.project.id}/sites/${data.site.id}`;
              const qp = {
                tab: 'daily-report',
                reportId: data.id,
                date: data.report_date,
              };
              const pageUrl = this.router.url.split('?')[0];
              if (url === pageUrl) {
                this.router.navigate(['/']).then(() => {
                  this.router.navigate([url], {
                    queryParams: qp,
                  });
                });
              } else {
                this.router.navigate([url], {
                  queryParams: qp,
                });
              }
            } else if (action.type === CreateDailyReportFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  checkIfDailyReportCanBeCreated$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CheckIfDailyReportCanBeCreatedRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchDailyReports(payload).pipe(
          map(response => {
            const { data } = response;

            const temp = data.filter(o => o.createdBy.id === payload.createdBy && o.site.id === payload.site);

            return CheckIfDailyReportCanBeCreatedSuccess({ existingReportId: temp[0]?.id });
          }),
          catchError(() => {
            return of(CheckIfDailyReportCanBeCreatedFailure());
          }),
          tap((action: any) => {
            if (action.type === CheckIfDailyReportCanBeCreatedSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === CheckIfDailyReportCanBeCreatedFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchProjectReports$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectReportsListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectReports(payload).pipe(
          map(response => {
            const { data } = response;

            return FetchProjectReportsListSuccess({ projectReportsList: data });
          }),
          catchError(() => {
            return of(FetchProjectReportsListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchProjectReportsListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchProjectReportsListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchProjectReport$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectReportRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectReports(payload).pipe(
          map(response => {
            const { data } = response;
            const cprIsPresent = !!data?.length;
            const bidItemsArr = [];
            let baseBidItemsArr = [];
            const consolidatedWeathersList = [];
            const consolidatedActivityList = [];

            data?.map(d => {
              d?.daily_reports?.forEach(dailyReport => {
                consolidatedWeathersList.push({
                  ...dailyReport?.weather,
                  dailyReportCreatedBy: dailyReport?.createdBy,
                  dailyReportId: dailyReport?.id,
                  site: dailyReport?.site,
                });

                dailyReport?.activities.forEach(eachItem => {
                  consolidatedActivityList.push({
                    ...eachItem,
                    dailyReportCreatedBy: dailyReport.createdBy,
                    dailyReportId: dailyReport.id,
                    site: dailyReport.site,
                  });
                });

                dailyReport?.bid_items.forEach(eachItem => {
                  bidItemsArr.push({
                    ...eachItem,
                    dailyReportCreatedBy: dailyReport.createdBy,
                    dailyReportId: dailyReport.id,
                    site: dailyReport.site,
                  });
                });
              });
            });

            const reducedBidItems = bidItemsArr.reduce((acc, current) => {
              if (acc[current.bid_item.id] !== undefined) {
                acc[current.bid_item.id].push(current);
              } else {
                acc[current.bid_item.id] = [current];
                baseBidItemsArr.push(JSON.parse(JSON.stringify(current.bid_item)));
              }

              return acc;
            }, {});

            baseBidItemsArr = baseBidItemsArr.map(o => {
              o.children = reducedBidItems[o.id];

              o.totalQuantity = 0;
              o.totalAmount = 0;

              o.status = {
                agree: 0,
                disagree: 0,
              };

              const sites = [];
              const createdBy = [];

              o.children = o?.children?.map(m => {
                m.status = m.contractor_status || 1;

                if (!m.contractor_status) {
                  o.status.agree = o.status.agree + 1;
                } else {
                  if (m.contractor_status === 'agree') {
                    m.status = 1;
                    o.status.agree = o.status.agree + 1;
                  } else if (m.contractor_status === 'disagree') {
                    m.status = 0;
                    o.status.disagree = o.status.disagree + 1;
                  }
                }

                o.totalQuantity += m.quantity;

                if (m.quantity > 0) {
                  m.totalAmount = m.quantity * m.bid_item.unit_price;
                  o.totalAmount += m.totalAmount;
                } else if (m.bid_item?.rollup && m.bid_item?.rollup_formula) {
                  const fields = m.bid_item?.fields || [];
                  const fieldValues = m?.field_values || [];
                  const rollupFormula = m.bid_item?.rollup_formula;

                  m.quantity = this.calculateRollupQuantity(fields, fieldValues, rollupFormula);
                  m.totalAmount = m.quantity * m.bid_item.unit_price;
                  o.totalAmount += m.totalAmount;
                  o.totalQuantity += +m.quantity;
                } else {
                  m.totalAmount = 0;
                }

                if (!sites.includes(m.site.id)) {
                  sites.push(m.site.id);
                }

                if (!createdBy.includes(m.dailyReportCreatedBy.id)) {
                  createdBy.push(m.dailyReportCreatedBy.id);
                }
                return m;
              });

              o.totalSites = sites?.length;
              o.createdByCount = createdBy?.length;

              return o;
            });

            baseBidItemsArr.forEach(baseBidItem => {
              baseBidItem.children.forEach(eachChild => {
                const combinedMedia = [];
                if (eachChild.contractor_pictures && eachChild.contractor_pictures?.length > 0) {
                  eachChild.contractor_pictures?.forEach(picture => {
                    picture.fileType = 'picture';
                    combinedMedia.push(picture);
                  });
                }
                if (eachChild.contractor_videos && eachChild.contractor_videos?.length > 0) {
                  eachChild.contractor_videos?.forEach(video => {
                    video.fileType = 'video';
                    combinedMedia.push(video);
                  });
                }

                eachChild.media = combinedMedia;
              });
            });

            return FetchProjectReportSuccess({
              projectReport: data[0],
              consolidatedBidItemsList: baseBidItemsArr,
              consolidatedWeathersList,
              consolidatedActivityList,
              cprIsPresent,
            });
          }),
          catchError(() => {
            return of(FetchProjectReportFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchProjectReportSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.closeRightPanel.next();
              if (action?.cprIsPresent) {
                const fetchIssuesRequestPayload = {
                  siteId: action?.dailyReport?.site_id,
                  qp: {
                    start: 0,
                    total: 1000,
                    projectId: payload?.qp?.projectId,
                  },
                };

                this.store.dispatch(FetchIssuesRequest({ payload: fetchIssuesRequestPayload }));
              }
            } else if (action.type === FetchProjectReportFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchPayappItems$ = createEffect(() =>
    this.actions.pipe(
      ofType(FetchPayappItemsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchPayappItems(payload).pipe(
          map(response => {
            const payAppItems = response.data;
            payAppItems.forEach(eachChild => {
              const combinedMedia = [];
              if (eachChild.contractor_pictures && eachChild.contractor_pictures?.length > 0) {
                eachChild.contractor_pictures?.forEach(picture => {
                  picture.fileType = 'picture';
                  combinedMedia.push(picture);
                });
              }
              if (eachChild.contractor_videos && eachChild.contractor_videos?.length > 0) {
                eachChild.contractor_videos?.forEach(video => {
                  video.fileType = 'video';
                  combinedMedia.push(video);
                });
              }

              eachChild.media = combinedMedia;
            });

            return FetchPayappItemsSuccess({ payappItems: payAppItems });
          }),
          catchError(() => of(FetchPayappItemsFailure())),
          tap(action => {
            this.commonService.stopLoading();

            if (action.type === FetchPayappItemsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchPayappItemsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchPayapps$ = createEffect(() =>
    this.actions.pipe(
      ofType(FetchPayappsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchPayapps(payload).pipe(
          map(response => FetchPayappsSuccess({ payapps: response.data })),
          catchError(() => of(FetchPayappsFailure())),
          tap(action => {
            this.commonService.stopLoading();

            if (action.type === FetchPayappsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchPayappsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchPayappdetails$ = createEffect(() =>
    this.actions.pipe(
      ofType(FetchPayappDetailsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchPayappDetails(payload).pipe(
          map(response => {
            const payappData = response.data;
            // Use line_items (from pay_app_line_item table) for G703 data
            // Fallback to bid_items for backward compatibility
            const { line_items, bid_items, id, name } = payappData;
            // Map line_items fields to match bid_items structure for frontend compatibility
            let payappItems = bid_items;
            if (line_items && line_items.length > 0) {
              payappItems = line_items.map(item => ({
                ...item,
                item: item.item_code, // Map item_code to item for frontend
                report_bid_item_id: item.uuid, // Map uuid to report_bid_item_id
              }));
            }
            return FetchPayappDetailsSuccess({
              payappItems,
              payappId: id,
              payappName: name,
              payappDetails: payappData, // Pass full payapp data for summary
            });
          }),
          catchError(() => of(FetchPayappDetailsFailure())),
          tap(action => {
            this.commonService.stopLoading();

            if (action.type === FetchPayappDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchPayappDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  generatePayapp$ = createEffect(() =>
    this.actions.pipe(
      ofType(GeneratePayappRequest),
      mergeMap(action => {
        this.commonService.startLoading();
        return this.projectsService.generatePayapp(action.payload).pipe(
          map(response => GeneratePayappSuccess({ data: response.data })),
          catchError(() => of(GeneratePayappFailure())),
          tap((action: any) => {
            this.commonService.stopLoading();
            if (action.type === GeneratePayappSuccess.type) {
              this.commonService.notification('Payapp generated successfully', 'success');
            } else if (action.type === GeneratePayappFailure.type) {
              console.error('Failed to generate pay app');
            }
          }),
          withLatestFrom(this.store.select(projectDetails)), // Combine with latest project details from store
          concatMap(([action, projectDetails]) => {
            if (action.type === GeneratePayappSuccess.type) {
              const projectId = projectDetails.id; // Retrieve projectId from store
              const generatedPayappId = action.data.id;

              // Step 1: Dispatch FetchPayappsRequest
              this.store.dispatch(
                FetchPayappsRequest({
                  payload: {
                    projectId, // Use projectId from store
                  },
                })
              );

              // Return an object that keeps projectId and generatedPayappId in scope
              return of({ projectId, generatedPayappId });
            } else {
              // If the action is not GeneratePayappSuccess, return an empty observable
              return EMPTY;
            }
          }),
          concatMap(({ projectId, generatedPayappId }) => {
            const qp = {
              includeDeferred: true,
              includeRejected: true,
            };

            this.store.dispatch(
              FetchPayappItemsRequest({
                payload: {
                  projectId,
                  qp,
                },
              })
            );
            return of({ projectId, generatedPayappId });
          }),
          concatMap(({ projectId, generatedPayappId }) => {
            // Step 2: Once prior requests completes, dispatch FetchPayappDetailsRequest
            return of(FetchPayappDetailsRequest({
              payload: {
                projectId,
                payappId: generatedPayappId,
                qp: {
                  // Use line_items for G703 data (includes quantity_previous, quantity_to_date, etc.)
                  include: ['line_items', 'calculated'],
                },
              },
            }));
          })
        );
      })
    )
  );

  siteDetailsReport$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSiteDetailsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchSiteDetails(payload).pipe(
          map(response => {
            const { data } = response;
            data.headings = data?.headings.map(item => {
              return {
                ...item,
                value: item.id,
                label: item.name,
              };
            });
            const siteDetails = {
              id: data?.id,
              name: data?.name,
              project: {
                id: data?.project?.id,
                name: data?.project?.name,
                owner: data?.project?.owner_organization?.name,
              },
              latitude: data?.latitude,
              longitude: data?.longitude,
              geometry: data?.geometry,
              imageUrl: data?.project?.cover_photo?.thumb_url,
              headings: data?.headings || [],
            };

            return FetchSiteDetailsSuccess({ siteDetails });
          }),
          catchError(() => {
            return of(FetchSiteDetailsFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchSiteDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchSiteDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchDailyReportsByDate$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchDailyReportsByDateRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchDailyReports(payload).pipe(
          map(response => {
            const { data } = response;

            return FetchDailyReportsByDateSuccess({ dailyReportsByDate: data });
          }),
          catchError(() => {
            return of(FetchDailyReportsByDateFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchDailyReportsByDateSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchDailyReportsByDateFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );
  saveDailyReport$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SaveDailyReportRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.saveDailyReport(payload).pipe(
          map(response => {
            void this.sheetMarkups.flush(response);
            // The new version: the next save from this tab sends it as base_updated_at.
            return SaveDailyReportSuccess({
              reportId: response?.data?.id ?? null,
              updatedAt: response?.data?.updated_at ?? null,
            });
          }),
          catchError((err) => {
            const conflict = reportChangedBody(err);
            if (conflict) {
              return of(SaveDailyReportFailure({ error: null, conflict }));
            }
            return of(SaveDailyReportFailure({ error: this.getDailyReportSaveErrorMessage(err) }));
          }),
          tap((action: any) => {
            if (action.type === SaveDailyReportSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.analyticsService.capture('daily_report_action', {
                action: 'saved',
                project_id: payload?.params?.projectId || this.route.snapshot.params?.projectId,
                site_id: payload?.params?.siteId || this.route.snapshot.params?.siteId,
              });
              this.projectsService.isAnyFormDirty.next(false);
              this.dailyReportService.fetchDailyReportById.next({ fromDialog: !!payload?.fromDialog });
              this.commonService.resetSearchContext.next(true);
              this.commonService.notification('Report saved successfully.', 'success');
            } else if (action.type === SaveDailyReportFailure.type && action.conflict) {
              // The report changed since this tab loaded it (e.g. on the mobile app): nothing
              // was written. No error toast; Refresh reloads it, Cancel keeps the form as is.
              this.openDailyReportChangedDialog(action.conflict);
            } else if (action.type === SaveDailyReportFailure.type) {
              this.commonService.notification(action.error, 'error');
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  copyDailyReport$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CopyDailyReportRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.saveDailyReport(payload).pipe(
          map(() => {
            return CopyDailyReportSuccess({ payload });
          }),
          catchError(() => {
            return of(CopyDailyReportFailure());
          }),
          tap((action: any) => {
            if (action.type === CopyDailyReportSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.isAnyFormDirty.next(false);
              this.dialog.closeAll();
              this.router.navigate(['/']).then(() => {
                const url = `/projects/${action?.payload?.dailyReport?.project?.id}/sites/${action?.payload?.dailyReport?.site?.id}`;
                this.router
                  .navigate([`${url}`], {
                    queryParams: {
                      tab: 'daily-report',
                      date: action?.payload?.dailyReport?.report_date,
                    },
                  })
                  .then(() => {
                    this.commonService.notification('Report copied successfully.', 'success');
                  });
              });
            } else if (action.type === CopyDailyReportFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchAllBidItems$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchAllBidItemsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectDetails(payload).pipe(
          map(response => {
            const { data } = response;
            const tempData = data?.bid_items?.map((bidItem: any) => {
              return {
                ...bidItem,
                label: bidItem.description,
                value: bidItem.id,
              };
            });
            return FetchAllBidItemsSuccess({
              allBidItems: data.bid_items,
              allBidItemsFilter: tempData,
            });
          }),
          catchError(() => {
            return of(FetchAllBidItemsFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchAllBidItemsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchAllBidItemsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchBidSheet$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchBidSheetRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchBidSheet(payload).pipe(
          map(response => {
            const { data } = response;
            return FetchBidSheetSuccess({
              bidSheet: data,
            });
          }),
          catchError(() => {
            return of(FetchBidSheetFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchBidSheetSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchBidSheetFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchSettingsBidSheet$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSettingsBidSheetRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchBidSheet(payload).pipe(
          map(response => {
            const { data } = response;
            return FetchSettingsBidSheetSuccess({
              settingsBidSheet: data,
            });
          }),
          catchError(() => {
            return of(FetchSettingsBidSheetFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchSettingsBidSheetSuccess.type) {
              this.store.dispatch(
                FetchSettingsBidItemsRequest({
                  payload: {
                    bidSheetId: action.settingsBidSheet?.id,
                    qp: { start: 0, total: 1000, include: ['site'] },
                  },
                })
              );
            } else if (action.type === FetchSettingsBidSheetFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchSettingsBidItems$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSettingsBidItemsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchBidItems(payload).pipe(
          map(res => {
            const list = res?.data || [];
            const totalRecords = res?.meta?.totalRecords ?? list.length;
            const sortedList = [...list].sort(
              (a, b) => new Date(b?.updated_at || 0).getTime() - new Date(a?.updated_at || 0).getTime()
            );
            return FetchSettingsBidItemsSuccess({
              settingsBidItemsListInfo: { list: sortedList, totalRecords },
            });
          }),
          catchError(() => of(FetchSettingsBidItemsFailure({}))),
          tap((action: any) => {
            if (action.type === FetchSettingsBidItemsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchSettingsBidItemsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchBidItemFields$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchBidItemFieldsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService.fetchBidItemFields({ bidItemId: action.bidItemId, qp: action.qp }).pipe(
          map(res => {
            const list = res?.data ?? res ?? [];
            return FetchBidItemFieldsSuccess({ list: Array.isArray(list) ? list : [] });
          }),
          catchError(() => of(FetchBidItemFieldsFailure({}))),
          tap(() => this.commonService.stopLoading())
        )
      )
    )
  );

  fetchSettingsBidItemDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSettingsBidItemDetailsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService
          .fetchSettingsBidItemDetails({ bidItemId: action.bidItemId, qp: action.qp })
          .pipe(
            map(response => {
              const data = response?.data ?? response;
              return FetchSettingsBidItemDetailsSuccess({
                bidItemId: action.bidItemId,
                bidItem: data,
              });
            }),
            catchError(() => of(FetchSettingsBidItemDetailsFailure({ bidItemId: action.bidItemId }))),
            tap((action: any) => {
              if (action.type === FetchSettingsBidItemDetailsSuccess.type) {
                // Code to execute on API Success Action dispatch
              } else if (action.type === FetchSettingsBidItemDetailsFailure.type) {
                // Code to execute on API Failure Action dispatch
              }
              this.commonService.stopLoading();
            })
          )
      )
    )
  );

  private readonly settingsBidItemDetailsQp = {
    include: [{ headings: ['alignment'] }, 'rollup_field', 'fields', 'materials', 'site_quantities'],
  };

  settingsCreateBidItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsCreateBidItemRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService
          .createBidItem({ bidSheetId: action.payload.bidSheetId, body: action.payload.body })
          .pipe(
            map(response => {
              const data = response?.data ?? response;
              return SettingsCreateBidItemSuccess({ bidItem: data });
            }),
            catchError(() => of(SettingsCreateBidItemFailure())),
            tap((resultAction: any) => {
              if (resultAction.type === SettingsCreateBidItemSuccess.type) {
                this.commonService.notification('Bid item created successfully', 'success');
                this.commonService.closeDialog.next();
              } else if (resultAction.type === SettingsCreateBidItemFailure.type) {
                this.commonService.notification('Failed to create bid item', 'error');
              }
              this.commonService.stopLoading();
            })
          )
      )
    )
  );

  settingsUpdateBidItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsUpdateBidItemRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService
          .updateBidItem({ bidItemId: action.payload.bidItemId, body: action.payload.body })
          .pipe(
            map(response => {
              const data = response?.data ?? response;
              return SettingsUpdateBidItemSuccess({ bidItem: data });
            }),
            catchError(() => of(SettingsUpdateBidItemFailure())),
            tap((resultAction: any) => {
              if (resultAction.type === SettingsUpdateBidItemSuccess.type) {
                this.commonService.notification('Bid item updated successfully', 'success');
                this.projectsService.isAnyFormDirty.next(false);
              } else if (resultAction.type === SettingsUpdateBidItemFailure.type) {
                this.commonService.notification('Failed to update bid item', 'error');
              }
              this.commonService.stopLoading();
            })
          )
      )
    )
  );

  settingsDeleteBidItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsDeleteBidItemRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService
          .deleteBidItem({ bidItemId: action.payload.bidItemId })
          .pipe(
            map(() => SettingsDeleteBidItemSuccess({ bidItemId: action.payload.bidItemId })),
            catchError(() => of(SettingsDeleteBidItemFailure())),
            tap((resultAction: any) => {
              if (resultAction.type === SettingsDeleteBidItemSuccess.type) {
                this.commonService.notification('Bid item deleted successfully', 'success');
                this.commonService.closeDialog.next();
              } else if (resultAction.type === SettingsDeleteBidItemFailure.type) {
                this.commonService.notification('Failed to delete bid item', 'error');
              }
              this.commonService.stopLoading();
            })
          )
      )
    )
  );

  settingsCreateBidItemField$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsCreateBidItemFieldRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService.createBidItemField(action.bidItemId, action.body).pipe(
          map(response => {
            const field = response?.data ?? response;
            return SettingsCreateBidItemFieldSuccess({ bidItemId: action.bidItemId, field });
          }),
          catchError(() => {
            this.commonService.notification('Failed to create field', 'error');
            return of(SettingsCreateBidItemFieldFailure());
          }),
          tap((action: any) => {
            if (action.type === SettingsCreateBidItemFieldSuccess.type) {
              this.commonService.notification('Field created successfully', 'success');
              this.store.dispatch(
                FetchSettingsBidItemDetailsRequest({
                  bidItemId: action.bidItemId,
                  qp: this.settingsBidItemDetailsQp,
                })
              );
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  settingsUpdateBidItemField$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsUpdateBidItemFieldRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService.updateBidItemField(action.fieldId, action.body).pipe(
          map(response => {
            const field = response?.data ?? response;
            return SettingsUpdateBidItemFieldSuccess({ bidItemId: action.bidItemId, field });
          }),
          catchError(() => {
            this.commonService.notification('Failed to update field', 'error');
            return of(SettingsUpdateBidItemFieldFailure());
          }),
          tap((action: any) => {
            if (action.type === SettingsUpdateBidItemFieldSuccess.type) {
              this.commonService.notification('Field updated successfully', 'success');
              this.store.dispatch(
                FetchSettingsBidItemDetailsRequest({
                  bidItemId: action.bidItemId,
                  qp: this.settingsBidItemDetailsQp,
                })
              );
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchSettingsProjectMaterialsList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSettingsProjectMaterialsListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) => {
        const qp = action.qp ?? { start: 0, total: 100000, 'include[]': '' };
        return this.projectsService
          .fetchSettingsProjectMaterialsList({ projectId: action.projectId, qp })
          .pipe(
            map(response => {
              const raw = response?.data ?? response;
              const list = Array.isArray(raw) ? raw : (raw?.list ?? []);
              return FetchSettingsProjectMaterialsListSuccess({ projectId: action.projectId, list });
            }),
            catchError(() =>
              of(FetchSettingsProjectMaterialsListFailure({ projectId: action.projectId }))
            ),
            tap((action: any) => {
              if (action.type === FetchSettingsProjectMaterialsListFailure.type) {
                this.commonService.notification('Failed to load materials list', 'error');
              }
              this.commonService.stopLoading();
            })
          );
      })
    )
  );

  settingsAddMaterialToBidItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsAddMaterialToBidItemRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService.addBidItemMaterial({ materialId: action.materialId, bidItemId: action.bidItemId }).pipe(
          map(() =>
            SettingsAddMaterialToBidItemSuccess({ materialId: action.materialId, bidItemId: action.bidItemId })
          ),
          catchError(() => {
            this.commonService.notification('Failed to add material to bid item', 'error');
            return of(SettingsAddMaterialToBidItemFailure());
          }),
          tap((action: any) => {
            if (action.type === SettingsAddMaterialToBidItemSuccess.type) {
              this.commonService.notification('Material added successfully', 'success');
              this.store.dispatch(
                FetchSettingsBidItemDetailsRequest({
                  bidItemId: action.bidItemId,
                  qp: this.settingsBidItemDetailsQp,
                })
              );
            } else if (action.type === SettingsAddMaterialToBidItemFailure.type) {
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  settingsRemoveMaterialFromBidItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsRemoveMaterialFromBidItemRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService.removeBidItemMaterial({ materialId: action.materialId, bidItemId: action.bidItemId }).pipe(
          map(() =>
            SettingsRemoveMaterialFromBidItemSuccess({ materialId: action.materialId, bidItemId: action.bidItemId })
          ),
          catchError(() => {
            this.commonService.notification('Failed to remove material from bid item', 'error');
            return of(SettingsRemoveMaterialFromBidItemFailure());
          }),
          tap((action: any) => {
            if (action.type === SettingsRemoveMaterialFromBidItemSuccess.type) {
              this.commonService.notification('Material removed successfully', 'success');
              this.store.dispatch(
                FetchSettingsBidItemDetailsRequest({
                  bidItemId: action.bidItemId,
                  qp: this.settingsBidItemDetailsQp,
                })
              );
            } else if (action.type === SettingsRemoveMaterialFromBidItemFailure.type) {
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  settingsUpdateBidItemSiteQuantity$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsUpdateBidItemSiteQuantityRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService
          .updateBidItemSiteQuantity({
            bidItemId: action.bidItemId,
            siteId: action.siteId,
            quantity: action.quantity,
          })
          .pipe(
            map(() =>
              SettingsUpdateBidItemSiteQuantitySuccess({
                bidItemId: action.bidItemId,
                siteId: action.siteId,
                quantity: action.quantity,
              })
            ),
            catchError(() => {
              this.commonService.notification('Failed to update site quantity', 'error');
              return of(SettingsUpdateBidItemSiteQuantityFailure());
            }),
            tap((action: any) => {
              if (action.type === SettingsUpdateBidItemSiteQuantitySuccess.type) {
                this.commonService.notification('Site quantity updated successfully', 'success');
                this.store.dispatch(
                  FetchSettingsBidItemDetailsRequest({
                    bidItemId: action.bidItemId,
                    qp: this.settingsBidItemDetailsQp,
                  })
                );
              } else if (action.type === SettingsUpdateBidItemSiteQuantityFailure.type) {
              }
              this.commonService.stopLoading();
            })
          )
      )
    )
  );

  fetchBidItems$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchBidItemsListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchBidItems(payload).pipe(
          map(response => {
            const { data, meta } = response;
            return FetchBidItemsListSuccess({
              bidItemsListInfo: { list: data, totalRecords: meta.totalRecords },
            });
          }),
          catchError(() => {
            return of(FetchBidItemsListFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchBidItemsListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchBidItemsListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  uploadChangeOrderFile$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UploadChangeOrderFileRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.uploadChangeOrderFile(payload).pipe(
          map(response => {
            const { data } = response;
            return UploadChangeOrderFileSuccess({
              changeOrderList: data,
            });
          }),
          catchError(error => {
            let displayError: any;
            if (error.error.status === 422) {
              displayError = Object.entries(error?.error?.error).map(([_, value]) => {
                const parts = String(value['param'] ?? '').split('.');
                // Row-level CSV errors have a "<rowIndex>.<column>" param; field-level
                // errors (e.g. mimetype, originalname) have a non-numeric param and must
                // not render as "Row NaN - Column :".
                const isRowError = parts.length > 1 && parts[0] !== '' && !isNaN(Number(parts[0]));
                return {
                  isRowError,
                  rowNumber: isRowError ? Number(parts[0]) + 1 : null,
                  columnName: isRowError ? parts[1] : '',
                  msg: value['msg'],
                };
              });
            }
            if (error.error.status === 500) {
              displayError = 'An Unknown error occurred, please check you file type';
            }
            this.projectsService.error.next(displayError);
            return of(UploadChangeOrderFileFailure({}));
          }),
          tap((action: any) => {
            if (action.type === UploadChangeOrderFileSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.closeDialog.next();
              this.commonService.openSuccessDialog.next();
            } else if (action.type === UploadChangeOrderFileFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  uploadSettingsBidSheetCsv$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UploadSettingsBidSheetCsvRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.uploadBidSheetCsv(payload).pipe(
          map(response => {
            const { data } = response;
            return UploadSettingsBidSheetCsvSuccess({ data });
          }),
          catchError(error => {
            let displayError: any;
            if (error?.error?.status === 422) {
              displayError = Object.entries(error?.error?.error || {}).map(([_, value]: [string, any]) => {
                const parts = String(value?.param ?? '').split('.');
                // Row-level CSV errors have a "<rowIndex>.<column>" param; field-level
                // errors (e.g. mimetype, originalname) have a non-numeric param and must
                // not render as "Row NaN - Column :".
                const isRowError = parts.length > 1 && parts[0] !== '' && !isNaN(Number(parts[0]));
                return {
                  isRowError,
                  rowNumber: isRowError ? Number(parts[0]) + 1 : null,
                  columnName: isRowError ? parts[1] : '',
                  msg: value?.msg ?? '',
                };
              });
            } else if (error?.error?.status === 500) {
              displayError = 'An unknown error occurred, please check your file type.';
            } else {
              displayError = error?.error?.message || error?.message || 'Upload failed.';
            }
            this.projectsService.error.next(displayError);
            return of(UploadSettingsBidSheetCsvFailure({}));
          }),
          tap((action: any) => {
            if (action.type === UploadSettingsBidSheetCsvSuccess.type) {
              this.commonService.closeDialog.next();
              if (payload?.bidSheetId) {
                this.store.dispatch(
                  FetchSettingsBidItemsRequest({
                    payload: {
                      bidSheetId: payload.bidSheetId,
                      qp: { start: 0, total: 1000, include: ['site'] },
                    },
                  })
                );
              }
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchBidItemsSitesList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchBidItemsSitesListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchBidItemsSitesList(payload).pipe(
          map(response => {
            const { data } = response;
            const { site_quantities, ...rest } = data;
            return FetchBidItemsSitesListSuccess({
              bidItemsSiteListInfo: {
                list: site_quantities,
                bidItemInfo: rest,
              },
            });
          }),
          catchError(() => {
            return of(FetchBidItemsSitesListFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchBidItemsSitesListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchBidItemsSitesListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchBidItemListWithChangeOrder$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchBidItemListWithChangeOrderRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchBidItemListWithChangeOrder(payload).pipe(
          map(response => {
            const { data } = response;
            const { bid_items } = data;
            const totalRecords = bid_items?.length;
            return FetchBidItemListWithChangeOrderSuccess({
              bidItemsListInfo: { list: bid_items, totalRecords },
            });
          }),
          catchError(() => {
            return of(FetchBidItemListWithChangeOrderFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchBidItemListWithChangeOrderSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchBidItemListWithChangeOrderFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchBidItemListWithChangeOrderInSideNav$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchBidItemListWithChangeOrderInSideNavRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchBidItemListWithChangeOrderInSideNav(payload).pipe(
          map(response => {
            const { data } = response;
            const { bid_items } = data;
            const { sites, ...rest } = bid_items.filter(o => o.bid_item_id === payload.bidItemId)[0];
            const sitesList = bid_items?.filter(bi => bi.bid_item_id === payload?.bidItemId)[0]?.sites;
            return FetchBidItemListWithChangeOrderInSideNavSuccess({
              bidItemsSiteListInfo: {
                list: sitesList,
                bidItemInfo: rest,
              },
            });
          }),
          catchError(() => {
            return of(FetchBidItemListWithChangeOrderInSideNavFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchBidItemListWithChangeOrderInSideNavSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchBidItemListWithChangeOrderInSideNavFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deletePayapp$ = createEffect(() =>
    this.actions.pipe(
      ofType(DeletePayappRequest),
      mergeMap(action => {
        this.commonService.startLoading();
        return this.projectsService.deletePayapp(action.payload.projectId, action.payload.payappId).pipe(
          map(response => DeletePayappSuccess({ data: response })),
          catchError(() => of(DeletePayappFailure())),
          tap((action: any) => {
            this.commonService.stopLoading();
            if (action.type === DeletePayappFailure.type) {
              this.commonService.notification('Failed to recall pay app', 'error');
            }
          }),
          withLatestFrom(this.store.select(projectDetails)),
          concatMap(([action, projectDetails]) => {
            if (action.type === DeletePayappSuccess.type) {
              const projectId = projectDetails?.id;

              // Step 1: Dispatch FetchPayappsRequest
              this.store.dispatch(
                FetchPayappsRequest({
                  payload: {
                    projectId,
                  },
                })
              );
              return of({ projectId });
            } else {
              return EMPTY;
            }
          }),
          concatMap(({ projectId }) => {
            const qp = {
              includeDeferred: true,
              includeRejected: true,
            };
            this.store.dispatch(
              FetchPayappItemsRequest({
                payload: {
                  projectId,
                  qp,
                },
              })
            );
            return of({ projectId });
          }),
          concatMap(({ projectId }) => {
            this.commonService.notification('Payapp recalled successfully', 'success');
            return of(DeletePayappSuccess({ data: projectId }));
          })
        );
      })
    )
  );

  updatePayappStatus$ = createEffect(() =>
    this.actions.pipe(
      ofType(UpdatePayappStatusRequest),
      mergeMap(action => {
        return this.projectsService.updatePayappStatus(action.payload).pipe(
          map(response => UpdatePayappStatusSuccess({ data: response.data })),
          catchError(() => of(UpdatePayappStatusFailure()))
        );
      })
    )
  );

  fetchIssueTypes$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchIssueTypesRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchIssueTypes(payload).pipe(
          map(response => {
            const { data } = response;

            const issueTypesLabelMap = {};

            data.forEach(item => {
              issueTypesLabelMap[item.type] = item.label;
            });

            return FetchIssueTypesSuccess({
              issueTypesLabelMap,
              issueTypesFilter: data?.map(o => ({ value: o.id, label: o.label, ...o })),
            });
          }),
          catchError(() => {
            return of(FetchIssueTypesFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchIssueTypesSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchIssueTypesFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchSubContractorFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSubContractorFilterRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchSubContractorFilter(payload).pipe(
          map(response => {
            const { data } = response;
            const tempData = data?.map(item => {
              return {
                ...item,
                value: item.id,
                label: item.name,
              };
            });
            return FetchSubContractorFilterSuccess({
              subContractorFilter: tempData,
            });
          }),
          catchError(() => {
            return of(FetchSubContractorFilterFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchSubContractorFilterSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchSubContractorFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchLaborFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchLaborFilterRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchLaborFilter(payload).pipe(
          map(response => {
            const { data } = response;
            const tempData = data?.map(item => {
              return {
                ...item,
                value: item.value,
                label: item.value,
              };
            });
            return FetchLaborFilterSuccess({
              laborFilter: tempData,
            });
          }),
          catchError(() => {
            return of(FetchLaborFilterFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchLaborFilterSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchLaborFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchEquipmentFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchEquipmentFilterRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchEquipmentFilter(payload).pipe(
          map(response => {
            const { data } = response;
            const tempData = data?.map(item => {
              return {
                ...item,
                value: item.value,
                label: item.value,
              };
            });
            return FetchEquipmentFilterSuccess({
              equipmentFilter: tempData,
            });
          }),
          catchError(() => {
            return of(FetchEquipmentFilterFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchEquipmentFilterSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchEquipmentFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateIssueDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateIssuesDetailsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateIssuesDetails(payload).pipe(
          map(response => {
            void this.sheetMarkups.flush(response);
            return UpdateIssuesDetailsSuccess({});
          }),
          catchError(err => {
            // Record-route link errors (e.g. a sheet revision that can no longer be linked) carry only a message.
            this.commonService.notification(this.getApiErrorMessage(err, 'Failed to save issue.'), 'error');
            return of(UpdateIssuesDetailsFailure());
          }),
          tap((action: any) => {
            if (action.type === UpdateIssuesDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.closeRightPanel.next();
              this.searchService.closeRightPanel.next();
              this.projectsService.isAnyFormDirty.next(false);
              this.issueService.fetchIssuesList.next(true);
              this.dailyReportService.fetchDailyReportById.next({});
            } else if (action.type === UpdateIssuesDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  addIssueDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(AddIssuesDetailsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.addIssuesDetails(payload).pipe(
          map(response => {
            void this.sheetMarkups.flush(response);
            return AddIssuesDetailsSuccess({});
          }),
          catchError(err => {
            this.commonService.notification(this.getApiErrorMessage(err, 'Failed to save issue.'), 'error');
            return of(AddIssuesDetailsFailure());
          }),
          tap((action: any) => {
            if (action.type === AddIssuesDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.closeRightPanel.next();
              this.projectsService.isAnyFormDirty.next(false);
              this.issueService.fetchIssuesList.next(true);
              this.dailyReportService.fetchDailyReportById.next({});
            } else if (action.type === AddIssuesDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  changeIssueStatus$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ChangeIssueStatusRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.changeIssueStatus(payload).pipe(
          map(() => {
            return ChangeIssueStatusSuccess({});
          }),
          catchError(() => {
            return of(ChangeIssueStatusFailure());
          }),
          tap((action: any) => {
            if (action.type === ChangeIssueStatusSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.closeRightPanel.next();
              this.projectsService.isAnyFormDirty.next(false);
              this.issueService.fetchIssuesList.next(true);
              this.dailyReportService.fetchDailyReportById.next({});
              this.commonService.closeDialog.next();
              this.commonService.notification('Issue status updated successfully', 'success');
            } else if (action.type === ChangeIssueStatusFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchChangeOrderFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchChangeOrderFilterRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchChangeOrderFilter(payload).pipe(
          map(response => {
            const { data } = response;
            const tempData = data?.map(item => {
              return {
                ...item,
              };
            });
            const sortedTempData = tempData.sort((a, b) => {
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            });
            return FetchChangeOrderFilterSuccess({
              list: sortedTempData,
            });
          }),
          catchError(() => {
            return of(FetchChangeOrderFilterFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchChangeOrderFilterSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchChangeOrderFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  executeCurrentActionStateRequest$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ExecuteCurrentActionStateRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.executeCurrentActionStateRequest(payload?.executeCurrentActionStatePayload).pipe(
          map(response => {
            const { data } = response;
            return ExecuteCurrentActionStateSuccess({ data });
          }),
          catchError(() => {
            return of(ExecuteCurrentActionStateFailure());
          }),
          tap((action: any) => {
            if (action.type === ExecuteCurrentActionStateSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.isAnyFormDirty.next(false);
              const projectId = payload.fetchNextActionsStatePayload.qp.project_id;
              const siteId = payload.fetchNextActionsStatePayload.qp.site_id;
              const url = `/projects/${projectId}/sites/${siteId}`;
              const qp = {
                tab: 'daily-report',
                reportId: action.data.id,
                date: action.data.report_date,
              };

              this.router.navigate(['/']).then(() => {
                this.router.navigate([url], {
                  queryParams: qp,
                });
              });
            } else if (action.type === ExecuteCurrentActionStateFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchExternalTests$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchExternalTestsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchExternalTests(payload).pipe(
          map(response => {
            // Check if data is an array and log its length
            const { data } = response;

            const openTests = [];
            const closedTests = [];

            data.forEach(item => {
              const statusInfo = materialTestStatusMap[item.status];
              const temp =
                item.status !== 'sent_to_lab' ? item.activity_log?.find(o => o.type === 'lab_scheduled') : null;
              const sentToLabEntry = item.activity_log?.find(o => o.type === 'sent_to_lab');

              const processedItem = {
                ...item,
                statusInfo,
                labScheduledDate: temp?.created_at || null,
                sentToLabDate: sentToLabEntry?.created_at || null,
              };

              if (statusInfo?.group === 'open') {
                openTests.push(processedItem);
              } else if (statusInfo?.group === 'closed') {
                closedTests.push(processedItem);
              }

              if (item.pictures.length) {
                item?.pictures?.forEach(picture => {
                  if (picture?.mime.includes('image/')) {
                    picture.fileType = 'picture';
                  } else {
                    picture.fileType = 'document';
                  }
                });
              }

              if (item.material_test_report?.attachments?.length) {
                item.material_test_report?.attachments?.forEach(picture => {
                  if (picture?.mime.includes('image/')) {
                    picture.fileType = 'picture';
                  } else {
                    picture.fileType = 'document';
                  }
                });
              }

              if (item.inspection_detail?.pictures?.length) {
                item.inspection_detail?.pictures?.forEach(picture => {
                  if (picture?.mime.includes('image/')) {
                    picture.fileType = 'picture';
                  } else {
                    picture.fileType = 'document';
                  }
                });
              }
            });

            let dr: any = {};

            // Fetch daily report and clone it
            this.store
              .select(dailyReport)
              .subscribe(report => {
                dr = JSON.parse(JSON.stringify(report)); // Clone the report to avoid mutation
              })
              .unsubscribe();

            dr.reportDetails = {
              ...dr.reportDetails,
              materialTests: {
                ...dr.reportDetails.materialTests,
                external: {
                  openTests: openTests?.sort((a, b) => {
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                  }),
                  closedTests: closedTests?.sort((a, b) => {
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                  }),
                },
              },
            };

            // Return the success action with the updated daily report
            return FetchExternalTestsSuccess({
              dailyReport: dr,
            });
          }),
          catchError(() => {
            return of(FetchExternalTestsFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchExternalTestsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchExternalTestsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  addContractorStatus$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(AddContractorStatusRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.addContractorStatus(payload).pipe(
          map(() => {
            return AddContractorStatusSuccess({});
          }),
          catchError(() => {
            return of(AddContractorStatusFailure());
          }),
          tap((action: any) => {
            if (action.type === AddContractorStatusSuccess.type) {
              // Code to execute on API Success Action dispatch
              // this.commonService.closeDialog.next();
              this.projectsService.addContractorStatusSuccess.next(true);
            } else if (action.type === AddContractorStatusFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchIssues$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchIssuesRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchIssues(payload).pipe(
          map(response => {
            const { data } = response;
            const consolidatedIssues = data.filter(o => o.entity_type === 'site');

            return FetchIssuesSuccess({
              consolidatedIssues,
            });
          }),
          catchError(() => {
            return of(FetchIssuesFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchIssuesSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchIssuesFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteVideoFromMuxDb$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteVideoFromMuxDbRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteVideoFromMuxDb(payload).pipe(
          map(() => {
            return DeleteVideoFromMuxDbSuccess({});
          }),
          catchError(() => {
            return of(DeleteVideoFromMuxDbFailure());
          }),
          tap((action: any) => {
            if (action.type === DeleteVideoFromMuxDbSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === DeleteVideoFromMuxDbFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  formType: any;
  fetchDailyReportById$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchDailyReportRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchDailyReport(payload).pipe(
          map(response => {
            const { data } = response;
            const { activities, weather, labor, equipment, internal_tests, activity_codes, control_forms, sub_contractors, notes } = data;

            let { issues } = data;

            // Restructure bid items so that they are grouped by bid item id and heading and
            // can be used further as the basis of creating the UI

            const bidItems: any = {};

            for (const currentBidItem of data.bid_items) {
              if (bidItems[`${currentBidItem?.bid_item.id}_${currentBidItem?.heading_id}`] === undefined) {
                bidItems[`${currentBidItem?.bid_item.id}_${currentBidItem?.heading_id}`] = [currentBidItem];
              } else {
                bidItems[`${currentBidItem?.bid_item.id}_${currentBidItem?.heading_id}`].push(currentBidItem);
              }
            }

            const bidItemsArr = [];

            for (const key in bidItems) {
              if (bidItems.hasOwnProperty(key)) {
                bidItemsArr.push({
                  id: bidItems?.[key]?.[0]?.bid_item?.id,
                  bidItem: bidItems?.[key]?.[0]?.bid_item,
                  heading: bidItems?.[key]?.[0]?.heading,
                  ...(bidItems?.[key]?.[0]?.bid_item?.record_by_station
                    ? { stations: bidItems?.[key] }
                    : { lineItems: bidItems?.[key] }),
                });
              }
            }

            bidItemsArr?.forEach(bidItem => {
              bidItem?.lineItems?.forEach(lineItem => {
                const combinedMedia = [];
                if (lineItem?.pictures && lineItem?.pictures?.length > 0) {
                  lineItem?.pictures?.forEach(picture => {
                    picture.fileType = 'picture';
                    combinedMedia.push(picture);
                  });
                }
                if (lineItem?.videos && lineItem?.videos?.length > 0) {
                  lineItem?.videos?.forEach(video => {
                    video.fileType = 'video';
                    combinedMedia.push(video);
                  });
                }

                lineItem.pictures = combinedMedia;
                if (lineItem?.sources?.length > 0) {
                  if (lineItem?.sources?.length > 1) {
                    lineItem.sourcesSection = {
                      heading: `${lineItem?.created_at}`,
                      subheading: `${lineItem?.sources?.length} Inspectors`,
                      sourcesList: [],
                    };
                  } else {
                    lineItem.sourcesSection = {
                      heading: `${lineItem?.created_at}`,
                      subheading: `${lineItem?.sources[0].source_inspector} (${lineItem?.sources[0].source_name})`,
                      sourcesList: [],
                    };
                  }

                  const { fields } = lineItem?.bid_item;

                  lineItem?.sources?.forEach(o => {
                    const fieldValues = {};
                    fields?.forEach(field => {
                      fieldValues[field?.id] = {
                        id: field?.id,
                        value: null,
                      };

                      o?.source_fields?.forEach(sourceField => {
                        for (const key in sourceField) {
                          if (field?.name === key) {
                            fieldValues[field.id] = {
                              id: field?.id,
                              value: sourceField[key],
                            };
                          }
                        }
                      });

                      o?.modified_fields?.forEach(modifiedField => {
                        for (const key in modifiedField) {
                          if (field?.name === key) {
                            fieldValues[field?.id] = {
                              id: field?.id,
                              value: modifiedField[key],
                            };
                          }
                        }
                      });
                    });

                    lineItem?.sourcesSection?.sourcesList.push({
                      ...o,
                      fieldValues: Object.values(fieldValues),
                    });
                  });
                }

                // If no sources but has direct geometry (GPS devices), create a pseudo-sourcesSection
                if (!lineItem?.sources?.length && lineItem?.geometry) {
                  const geom = lineItem.geometry;
                  const features = geom.type === 'FeatureCollection' ? (geom.features || []) :
                                   geom.type === 'Feature' ? [geom] :
                                   geom.coordinates ? [{ type: 'Feature', geometry: geom, properties: {} }] : [];

                  const sourcesList = features
                    .filter(f => f.geometry)
                    .map((feature, index) => ({
                      id: feature.properties?.id || `gps_${index}`,
                      source_name: 'GPS',
                      source_inspector: feature.properties?.created_by || 'GPS Device',
                      source_geometry: feature.geometry,
                      source_quantity: null,
                      measurements: feature.properties?.source?.measurements || null,
                      gps_meta: feature.properties?.source?.meta || null,
                      modified_fields: feature.properties?.modified_fields || null,
                    }));

                  if (sourcesList.length) {
                    lineItem.sourcesSection = {
                      heading: `${lineItem?.created_at}`,
                      subheading: 'GPS Device',
                      sourcesList,
                    };
                  }
                }
              });
            });

            // Restructure material_deliveries into delivery and usage sections
            const formattedMaterialDelivery = this.dailyReportService?.formatDeliveriesAndUsages(
              data?.material_deliveries
            );

            formattedMaterialDelivery?.deliveries?.forEach(eachDelivery => {
              const combinedMedia = [];
              if (eachDelivery?.pictures && eachDelivery?.pictures?.length > 0) {
                eachDelivery?.pictures?.forEach(picture => {
                  picture.fileType = 'picture';
                  combinedMedia.push(picture);
                });
              }
              if (eachDelivery?.videos && eachDelivery?.videos?.length > 0) {
                eachDelivery?.videos?.forEach(video => {
                  video.fileType = 'video';
                  combinedMedia.push(video);
                });
              }

              eachDelivery.media = combinedMedia;
            });

            formattedMaterialDelivery.usages.forEach(eachUsage => {
              const combinedMedia = [];
              if (eachUsage?.pictures && eachUsage?.pictures?.length > 0) {
                eachUsage?.pictures?.forEach(picture => {
                  picture.fileType = 'picture';
                  combinedMedia.push(picture);
                });
              }
              if (eachUsage?.videos && eachUsage?.videos?.length > 0) {
                eachUsage?.videos?.forEach(video => {
                  video.fileType = 'video';
                  combinedMedia.push(video);
                });
              }

              eachUsage.media = combinedMedia;
            });

            let issuesScopeOptions = [
              {
                ...data.project,
                type: 'project',
              },
              {
                ...data.site,
                type: 'site',
              },
            ];
            issuesScopeOptions = issuesScopeOptions.map(scope => {
              return {
                label: scope.name,
                value: scope.id,
                type: scope.type,
              };
            });

            issues = issues.map(eachIssue => {
              return {
                ...eachIssue,
                issuesScopeOptions,
                projectId: data?.project?.id,
              };
            });

            // Restructure material_deliveries into delivery and usage sections

            const delivery = [];
            const usage = [];

            // tslint:disable-next-line:prefer-for-of
            for (let i = 0; i < data?.material_deliveries?.length; i++) {
              const element = data?.material_deliveries[i];

              if (
                (element.bid_item !== null && element.material === null) ||
                (element.bid_item === null && element.material !== null && element.received !== null) ||
                (element.bid_item !== null && element.material !== null && element.received !== null)
              ) {
                delivery.push(element);
              }

              if (
                (element.bid_item === null && element.material !== null && element.used !== null) ||
                (element.bid_item !== null && element.material !== null && element.used !== null)
              ) {
                usage.push(element);
              }
            }

            activities.forEach(activity => {
              const combinedMedia = [];
              if (activity?.pictures && activity?.pictures?.length > 0) {
                activity?.pictures?.forEach(picture => {
                  picture.fileType = 'picture';
                  combinedMedia.push(picture);
                });
              }
              if (activity?.videos && activity?.videos?.length > 0) {
                activity?.videos?.forEach(video => {
                  video.fileType = 'video';
                  combinedMedia.push(video);
                });
              }

              activity.media = combinedMedia;
            });

            activity_codes?.forEach(ac => {
              const combinedMedia = [];
              if (ac?.pictures && ac?.pictures?.length > 0) {
                ac?.pictures?.forEach(picture => {
                  picture.fileType = 'picture';
                  combinedMedia.push(picture);
                });
              }

              if (ac?.videos && ac?.videos?.length > 0) {
                ac?.videos?.forEach(video => {
                  video.fileType = 'video';
                  combinedMedia.push(video);
                });
              }

              ac.media = combinedMedia;
            });

            issues.forEach(issue => {
              const combinedMedia = [];
              if (issue?.pictures && issue?.pictures?.length > 0) {
                issue?.pictures?.forEach(picture => {
                  picture.fileType = 'picture';
                  combinedMedia.push(picture);
                });
              }
              if (issue?.videos && issue?.videos?.length > 0) {
                issue?.videos?.forEach(video => {
                  video.fileType = 'video';
                  combinedMedia.push(video);
                });
              }

              issue.media = combinedMedia;
            });

            let controlFormDetails: any = [];
            this.store
              .select(projectDetails)
              .subscribe(pd => {
                controlFormDetails = pd.control_forms;
              })
              .unsubscribe();

            control_forms?.forEach((controlForm: any) => {
              const combinedMedia = [];
              if (controlForm?.pictures && controlForm?.pictures?.length > 0) {
                controlForm?.pictures?.forEach(picture => {
                  picture.fileType = 'picture';
                  combinedMedia.push(picture);
                });
              }

              if (controlForm?.videos && controlForm?.videos?.length > 0) {
                controlForm?.videos?.forEach(video => {
                  video.fileType = 'video';
                  combinedMedia.push(video);
                });
              }

              controlForm.media = combinedMedia;

              controlForm?.lines.forEach(line => {
                const consolidatedMedia = [];
                if (line?.pictures && line?.pictures?.length > 0) {
                  line?.pictures?.forEach(picture => {
                    picture.fileType = 'picture';
                    consolidatedMedia.push(picture);
                  });
                }

                if (line?.videos && line?.videos?.length > 0) {
                  line?.videos?.forEach(video => {
                    video.fileType = 'video';
                    consolidatedMedia.push(video);
                  });
                }

                line.media = consolidatedMedia;
              });

              const cfId = controlForm?.control_form?.id;
              const cfd = controlFormDetails?.find(o => o?.id === cfId);

              controlForm.control_form = {
                ...controlForm?.control_form,
                ...cfd,
              };
            });

            control_forms?.forEach(cf => {
              cf.segregateLinesBySection = this.segregateLinesBySection(cf?.control_form?.fields, cf?.lines);
            });

            const controlPointForms = [];
            const testingForms = [];
            const dailyReportForms = [];

            control_forms?.forEach(controlForm => {
              if (controlForm?.control_form?.display_widget === 'forms') {
                dailyReportForms?.push(controlForm);
              } else if (controlForm?.control_form?.display_widget === 'control_forms') {
                controlPointForms?.push(controlForm);
              } else if (controlForm?.control_form?.display_widget === 'testing_forms') {
                testingForms?.push(controlForm);
              }
            });

            let pDInternalTests: any = {};

            this.store
              .select(projectDetails)
              .subscribe(details => {
                pDInternalTests = details?.internal_tests;
              })
              .unsubscribe();

            internal_tests.forEach(test => {
              const combinedMedia = [];
              if (test?.pictures && test?.pictures?.length > 0) {
                test?.pictures?.forEach(picture => {
                  picture.fileType = 'picture';
                  combinedMedia?.push(picture);
                });
              }
              if (test?.videos && test?.videos?.length > 0) {
                test?.videos?.forEach(video => {
                  video.fileType = 'video';
                  combinedMedia?.push(video);
                });
              }

              test.media = combinedMedia;
            });

            internal_tests?.forEach((it: any) => {
              const filteredPdInternalTestsFields = pDInternalTests?.find(
                (pdTest: any) => pdTest?.id === it?.internal_test_id
              )?.fields;

              it.field_values = it?.field_values?.map((fieldValue: any) => {
                const matchingField = filteredPdInternalTestsFields?.find((field: any) => field?.id === fieldValue?.id);

                if (matchingField) {
                  return {
                    ...matchingField,
                    value: fieldValue?.value,
                  };
                }

                return fieldValue;
              });
            });

            internal_tests?.forEach((it: any) => {
              const fieldValueSummaryArray = [];
              it?.field_values?.forEach((field: any) => {
                const value = `${field?.value} ${field?.uom || ''}`?.trim();
                fieldValueSummaryArray?.push(value);
              });
              it.fieldValueSummary = fieldValueSummaryArray?.join(', ');
            });

            const groupedByInternalTestId = {};
            // tslint:disable-next-line:prefer-for-of
            for (let i = 0; i < internal_tests?.length; i++) {
              const key = `${internal_tests[i]?.internal_test_id}`;
              if (groupedByInternalTestId[key]) {
                groupedByInternalTestId[key]?.push(internal_tests[i]);
              } else {
                groupedByInternalTestId[key] = [internal_tests[i]];
              }
            }

            const groupedByInternalTestIdCount = Object.keys(groupedByInternalTestId)?.length;

            let internalTestsWithStations = internal_tests?.filter(o => !!o.station);
            let internalTestsWithoutStations = internal_tests?.filter(o => !o.station);

            internalTestsWithoutStations = internalTestsWithoutStations?.reduce((rv, x) => {
              if (rv[`${x.internal_test_id}_${x.heading?.id}`]) {
                rv[`${x.internal_test_id}_${x.heading?.id}`]?.push(x);
              } else {
                rv[`${x.internal_test_id}_${x.heading?.id}`] = [x];
              }

              return rv;
            }, {});

            internalTestsWithStations = internalTestsWithStations?.reduce((rv, x) => {
              if (rv[`${x.internal_test_id}_${x.heading?.id}`]) {
                rv[`${x.internal_test_id}_${x.heading?.id}`]?.push(x);
              } else {
                rv[`${x.internal_test_id}_${x.heading?.id}`] = [x];
              }

              return rv;
            }, {});

            const groupings = {};

            // tslint:disable-next-line:forin
            for (const key in internalTestsWithStations) {
              const arr = [...internalTestsWithStations[key]];

              groupings[key] = arr.reduce((rv, x) => {
                if (rv[`${key}_${x.station?.id}`]) {
                  rv[`${key}_${x.station?.id}`].push(x);
                } else {
                  rv[`${key}_${x.station?.id}`] = [x];
                }

                return rv;
              }, {});
            }

            // tslint:disable-next-line:forin
            for (const key in groupings) {
              groupings[key] = Object.values(groupings[key]) || [];
            }

            internalTestsWithStations = Object.values(groupings) || [];
            internalTestsWithoutStations = Object.values(internalTestsWithoutStations) || [];

            internalTestsWithStations = internalTestsWithStations.map((tests: any) => {
              const testData: any = {
                type: 'withStations',
              };

              tests.forEach(o => {
                testData.id = `${o[0]?.heading?.id}_${o[0].internal_test?.id}`;
                testData.testId = o[0]?.internal_test?.id;
                testData.testName = o[0]?.internal_test?.name;
                testData.bidItemId = o[0]?.bid_item ? o[0].bid_item?.id : null;
                testData.bidItemName = o[0]?.bid_item
                  ? `Bid Item ${o[0].bid_item?.item} ${o[0].bid_item?.description}`
                  : null;
                testData.headingId = o[0]?.heading?.id;
                testData.headingName = o[0]?.heading?.name;
              });

              testData.list = tests?.map(l => {
                const childData: any = {};

                childData.stationId = l[0]?.station.id;
                childData.stationName = l[0]?.station.name;
                childData.station = l[0]?.station;
                childData.list = l;

                return childData;
              });

              return testData;
            });

            internalTestsWithoutStations = internalTestsWithoutStations?.map((tests: any) => {
              const testData: any = {
                type: 'withoutStations',
              };

              testData.id = `${tests[0]?.heading?.id}_${tests[0]?.internal_test?.id}`;
              testData.testId = tests[0]?.internal_test?.id;
              testData.testName = tests[0]?.internal_test?.name;
              testData.bidItemId = tests[0]?.bid_item ? tests[0].bid_item?.id : null;
              testData.bidItemName = tests[0]?.bid_item
                ? `Bid Item ${tests[0].bid_item?.item} ${tests[0].bid_item?.description}`
                : null;
              testData.headingId = tests[0]?.heading?.id;
              testData.headingName = tests[0]?.heading?.name;

              testData.list = tests;

              return testData;
            });

            sub_contractors?.forEach(sc => {
              const combinedMedia = [];
              if (sc?.pictures?.length) {
                sc.pictures.forEach(picture => {
                  picture.fileType = 'picture';
                  combinedMedia.push(picture);
                });
              }
              if (sc?.videos?.length) {
                sc.videos.forEach(video => {
                  video.fileType = 'video';
                  combinedMedia.push(video);
                });
              }
              sc.media = combinedMedia;
            });

            return FetchDailyReportSuccess({
              dailyReport: {
                ...data,
                site_id: data?.site_id || data?.site?.id,
                project_id: data?.project_id || data?.project?.id || data?.site?.project?.id || data?.site?.project_id,
                created_by: data?.created_by || data?.createdBy?.id,
                reportDetails: {
                  bidItems: this.dailyReportService.formatBidItems(data?.bid_items),
                  activities,
                  notes: notes || [],
                  activity_codes,
                  controlForms: {
                    controlPointForms,
                    dailyReportForms,
                    testingForms,
                  },
                  weather,
                  deliveries: formattedMaterialDelivery?.deliveries,
                  usages: formattedMaterialDelivery?.usages,
                  issues,
                  labors: labor,
                  equipments: equipment,
                  sub_contractors: sub_contractors || [],
                  materialDelivery: {
                    delivery,
                    usage,
                  },
                  materialTests: {
                    internal: {
                      groupedInternalTests: [...internalTestsWithStations, ...internalTestsWithoutStations],
                      groupedByInternalTestIdCount,
                    },
                  },
                },
              },
            });
          }),
          catchError(() => {
            return of(FetchDailyReportFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchDailyReportSuccess.type) {
              // Code to execute on API Success Action dispatch
              if (!payload?.fromDialog) {
                this.projectsService.closeRightPanel.next();
              }

              const fetchExternalTestsRequestPayload = {
                siteId: action?.dailyReport?.site?.id,
                qp: {
                  start: 0,
                  total: 1000,
                },
              };

              this.store.dispatch(FetchExternalTestsRequest({ payload: fetchExternalTestsRequestPayload }));
            } else if (action.type === FetchDailyReportFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchActivityIdsList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchActivityIdsListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchActivityIdsList(payload).pipe(
          map(response => {
            const { data, meta } = response;

            data.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

            return FetchActivityIdsListSuccess({
              activityIdsListInfo: { list: data, totalRecords: meta?.totalRecords },
            });
          }),
          catchError(() => {
            return of(FetchActivityIdsListFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchActivityIdsListSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.clearRowSelection.next();
            } else if (action.type === FetchActivityIdsListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  uploadActivityIdsFile$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UploadActivityIdsFileRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.uploadActivityIdsFile(payload).pipe(
          map(response => {
            const { data } = response;
            return UploadActivityIdsFileSuccess({
              activityIdsList: data,
            });
          }),
          catchError(() => {
            return of(UploadActivityIdsFileFailure({}));
          }),
          tap((action: any) => {
            if (action.type === UploadActivityIdsFileSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.closeDialog.next();
              this.commonService.openSuccessDialog.next();
            } else if (action.type === UploadActivityIdsFileFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateActivityIdsDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateActivityIdsDetailsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateActivityIdsDetails(payload).pipe(
          map(() => {
            return UpdateActivityIdsDetailsSuccess({});
          }),
          catchError(() => {
            return of(UpdateActivityIdsDetailsFailure({}));
          }),
          tap((action: any) => {
            if (action.type === UpdateActivityIdsDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
              const tempPayload = {
                projectId: payload.projectId,
                qp: {
                  start: 0,
                  total: 10000,
                },
              };

              this.store.dispatch(FetchActivityIdsListRequest({ payload: tempPayload }));

              this.projectsService.closeRightPanel.next();
            } else if (action.type === UpdateActivityIdsDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteActivityIdsDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteActivityIdsDetailsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteActivityIdsDetails(payload).pipe(
          map(() => {
            return DeleteActivityIdsDetailsSuccess({});
          }),
          catchError(() => {
            return of(DeleteActivityIdsDetailsFailure({}));
          }),
          tap((action: any) => {
            if (action.type === DeleteActivityIdsDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
              const tempPayload = {
                projectId: payload.projectId,
                qp: {
                  start: 0,
                  total: 10,
                },
              };

              this.store.dispatch(FetchActivityIdsListRequest({ payload: tempPayload }));

              this.commonService.clearRowSelection.next();
              this.projectsService.closeRightPanel.next();
            } else if (action.type === DeleteActivityIdsDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchDailyReportLineage$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchDailyReportLineageRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload => {
        // Create a mutable copy of the pagination object
        const paginationCopy = { ...payload.pagination };
        const qpCopy = { ...payload.qp }; // Create a copy of the query params too

        return this.workflowService.fetchLineage(payload.ex, paginationCopy, qpCopy).pipe(
          map(response => {
            this.commonService.stopLoading();
            return FetchDailyReportLineageSuccess({
              dailyReportLineage: response,
            });
          }),
          catchError(() => {
            this.commonService.stopLoading();
            return of(FetchDailyReportLineageFailure());
          })
        );
      })
    )
  );

  fetchBasisOfPayOptions$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchBasisOfPayOptionsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchBasisOfPayOptions(payload).pipe(
          map(response => {
            const { data } = response;
            const basisOfPayOptions = data?.map(o => {
              return {
                ...o,
                label: o.value,
              };
            });

            return FetchBasisOfPayOptionsSuccess({ basisOfPayOptions });
          }),
          catchError(() => {
            return of(FetchBasisOfPayOptionsFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchBasisOfPayOptionsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchBasisOfPayOptionsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchChangeInWorkCodeOptions$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchChangeInWorkCodeOptionsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchChangeInWorkCodeOptions(payload).pipe(
          map(response => {
            const { data } = response;

            const changeInWorkCodeOptions = data?.map(o => {
              return {
                ...o,
                label: o.value,
              };
            });
            return FetchChangeInWorkCodeOptionsSuccess({ changeInWorkCodeOptions });
          }),
          catchError(() => {
            return of(FetchChangeInWorkCodeOptionsFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchChangeInWorkCodeOptionsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchChangeInWorkCodeOptionsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchChangeInWorkRequestList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchChangeInWorkRequestListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchChangeInWorkRequestList(payload).pipe(
          map(response => {
            const { data, meta } = response;
            data.forEach(o => {
              const combinedMedia: any[] = [];

              if (o?.pictures && o?.pictures?.length > 0) {
                o.pictures.forEach((picture: any) => {
                  picture.fileType = 'picture';
                  combinedMedia.push(picture);
                });
              }

              if (o?.videos && o?.videos?.length > 0) {
                o.videos.forEach((video: any) => {
                  video.fileType = 'video';
                  combinedMedia.push(video);
                });
              }

              o.media = combinedMedia;

              const totalWorkDaysChange: number[] = [];
              const totalAmountArray: number[] = [];

              if (o?.lines && o?.lines?.length > 0) {
                o.lines.forEach((line: any) => {
                  const totalAmount = line?.original_unit_price != null
                    ? (line.unit_price || 0) * ((line.original_quantity || 0) + (line.quantity || 0))
                      - (line.original_unit_price || 0) * (line.original_quantity || 0)
                    : (line.unit_price || 0) * (line.quantity || 0);
                  totalAmountArray.push(totalAmount);
                  totalWorkDaysChange.push(line.additional_working_days || 0);
                });
              }

              o.totalAmount = totalAmountArray.reduce((sum, val) => sum + val, 0);
              o.totalWorkDaysChange = totalWorkDaysChange.reduce((sum, val) => sum + val, 0);
            });

            data.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

            return FetchChangeInWorkRequestListSuccess({
              // `loaded` lets the list tell a real (possibly empty) response
              // apart from the reducer's reset-on-request initial state
              changeInWorkRequestListInfo: { list: data, totalRecords: meta?.totalRecords, loaded: true },
            });
          }),
          catchError(() => {
            return of(FetchChangeInWorkRequestListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchChangeInWorkRequestListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchChangeInWorkRequestListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchChangeOrdersRequestList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchChangeOrdersRequestListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchChangeOrdersRequestList(payload).pipe(
          map(response => {
            const { data, meta } = response;
            data.forEach(o => {
              const combinedMedia: any[] = [];

              if (o?.pictures && o?.pictures?.length > 0) {
                o.pictures.forEach((picture: any) => {
                  picture.fileType = 'picture';
                  combinedMedia.push(picture);
                });
              }

              if (o?.videos && o?.videos?.length > 0) {
                o.videos.forEach((video: any) => {
                  video.fileType = 'video';
                  combinedMedia.push(video);
                });
              }

              o.media = combinedMedia;

              const totalWorkDaysChange: number[] = [];
              const totalAmountArray: number[] = [];

              if (o?.lines && o?.lines?.length > 0) {
                o.lines.forEach((line: any) => {
                  const totalAmount = line?.original_unit_price != null
                    ? (line.unit_price || 0) * ((line.original_quantity || 0) + (line.quantity || 0))
                      - (line.original_unit_price || 0) * (line.original_quantity || 0)
                    : (line.unit_price || 0) * (line.quantity || 0);
                  totalAmountArray.push(totalAmount);
                  totalWorkDaysChange.push(line.additional_working_days || 0);
                });
              }

              o.totalAmount = totalAmountArray.reduce((sum, val) => sum + val, 0);
              o.totalWorkDaysChange = totalWorkDaysChange.reduce((sum, val) => sum + val, 0);
            });

            data.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

            return FetchChangeOrdersRequestListSuccess({
              changeOrdersRequestListInfo: { list: data, totalRecords: meta?.totalRecords },
            });
          }),
          catchError(() => {
            return of(FetchChangeOrdersRequestListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchChangeOrdersRequestListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchChangeOrdersRequestListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  saveChangeInWorkRequest$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SaveChangeInWorkRequestRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.saveChangeInWorkRequest(payload?.payload).pipe(
          map(response => {
            const { data } = response;

            const combinedMedia: any[] = [];
            const totalWorkDaysChange: number[] = [];
            const totalAmountArray: number[] = [];

            if (data?.pictures && data?.pictures?.length > 0) {
              data.pictures.forEach((picture: any) => {
                picture.fileType = 'picture';
                combinedMedia.push(picture);
              });
            }

            if (data?.videos && data?.videos?.length > 0) {
              data.videos.forEach((video: any) => {
                video.fileType = 'video';
                combinedMedia.push(video);
              });
            }

            data.media = combinedMedia;

            if (data?.lines && data?.lines?.length > 0) {
              data.lines.forEach((line: any) => {
                const totalAmount = line?.original_unit_price != null
                    ? (line.unit_price || 0) * ((line.original_quantity || 0) + (line.quantity || 0))
                      - (line.original_unit_price || 0) * (line.original_quantity || 0)
                    : (line.unit_price || 0) * (line.quantity || 0);
                totalAmountArray.push(totalAmount);
                totalWorkDaysChange.push(line.additional_working_days || 0);
              });
            }

            data.totalAmount = totalAmountArray.reduce((sum, val) => sum + val, 0);
            data.totalWorkDaysChange = totalWorkDaysChange.reduce((sum, val) => sum + val, 0);

            return SaveChangeInWorkRequestSuccess({ data });
          }),
          catchError((err) => {
            const error = typeof err === 'function' ? err() : err;
            let errorMessage = 'Failed to save change order request';
            const errorBody = error?.error;
            if (typeof errorBody?.error === 'string') {
              errorMessage = errorBody.error;
            } else if (typeof errorBody?.error === 'object' && errorBody?.error) {
              const messages = Object.values(errorBody.error)
                .map((field: any) => field?.msg)
                .filter(Boolean);
              if (messages.length) {
                errorMessage = messages.join(', ');
              }
            } else if (errorBody?.message) {
              errorMessage = errorBody.message;
            }
            this.commonService.notification(errorMessage, 'error');
            return of(SaveChangeInWorkRequestFailure());
          }),
          tap((action: any) => {
            if (action.type === SaveChangeInWorkRequestSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.notification('Change order request saved successfully.', 'success');

              // A pending new item carries one shared price across every
              // request using it — name the other requests the save just moved
              const propagation: { request_no: string; count: number }[] =
                action?.data?.price_propagation || [];
              if (propagation.length) {
                const lines = propagation.reduce((s, p) => s + (p.count || 0), 0);
                const requests = propagation.map(p => p.request_no).join(', ');
                this.commonService.notification(
                  `Price updated on ${lines} pending line${lines === 1 ? '' : 's'} in request${propagation.length === 1 ? '' : 's'} ${requests}`,
                  'success',
                );
              }

              this.projectsService.isAnyFormDirty.next(false);

              this.store.dispatch(
                FetchChangeInWorkRequestListRequest({
                  payload: {
                    projectId: action?.data?.project?.id,
                    qp: {
                      start: 0,
                      total: 10000,
                    },
                  },
                })
              );
              this.projectsService.changeCurrentPageNumber.next(1);
              this.projectsService.createRequest.next({ type: false, data: action.data });

              if (payload.type === 'submit') {
                this.router
                  .navigate([], {
                    queryParams: {
                      subTab: 'ciwr',
                      snSubTab: 'workflow',
                    },
                    queryParamsHandling: 'merge',
                  })
                  .then(() => {
                    this.dialog.open(ChangeInWorkRequestReportComponent, {
                      width: '1000px',
                      height: '1100px',
                      data: {
                        ...action.data,
                      },
                    });
                  });
              } else {
                this.router.navigate([], {
                  queryParams: {
                    ciwr: action?.data?.id,
                  },
                  queryParamsHandling: 'merge',
                });
              }
            } else if (action.type === SaveChangeInWorkRequestFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  saveChangeOrdersRequest$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SaveChangeOrdersRequestRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.saveChangeOrdersRequest(payload?.payload).pipe(
          map(response => {
            const { data } = response;

            const combinedMedia: any[] = [];
            const totalWorkDaysChange: number[] = [];
            const totalAmountArray: number[] = [];

            if (data?.pictures && data?.pictures?.length > 0) {
              data.pictures.forEach((picture: any) => {
                picture.fileType = 'picture';
                combinedMedia.push(picture);
              });
            }

            if (data?.videos && data?.videos?.length > 0) {
              data.videos.forEach((video: any) => {
                video.fileType = 'video';
                combinedMedia.push(video);
              });
            }

            data.media = combinedMedia;

            if (data?.lines && data?.lines?.length > 0) {
              data.lines.forEach((line: any) => {
                const totalAmount = line?.original_unit_price != null
                    ? (line.unit_price || 0) * ((line.original_quantity || 0) + (line.quantity || 0))
                      - (line.original_unit_price || 0) * (line.original_quantity || 0)
                    : (line.unit_price || 0) * (line.quantity || 0);
                totalAmountArray.push(totalAmount);
                totalWorkDaysChange.push(line.additional_working_days || 0);
              });
            }

            data.totalAmount = totalAmountArray.reduce((sum, val) => sum + val, 0);
            data.totalWorkDaysChange = totalWorkDaysChange.reduce((sum, val) => sum + val, 0);

            return SaveChangeOrdersRequestSuccess({ data });
          }),
          catchError((err) => {
            const error = typeof err === 'function' ? err() : err;
            let errorMessage = 'Failed to save change order request';
            const errorBody = error?.error;
            if (typeof errorBody?.error === 'string') {
              errorMessage = errorBody.error;
            } else if (typeof errorBody?.error === 'object' && errorBody?.error) {
              const messages = Object.values(errorBody.error)
                .map((field: any) => field?.msg)
                .filter(Boolean);
              if (messages.length) {
                errorMessage = messages.join(', ');
              }
            } else if (errorBody?.message) {
              errorMessage = errorBody.message;
            }
            this.commonService.notification(errorMessage, 'error');
            return of(SaveChangeOrdersRequestFailure());
          }),
          tap((action: any) => {
            if (action.type === SaveChangeOrdersRequestSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.notification('Change order request saved successfully.', 'success');
              this.projectsService.isAnyFormDirty.next(false);

              this.store.dispatch(
                FetchChangeOrdersRequestListRequest({
                  payload: {
                    projectId: action?.data?.project?.id,
                    qp: {
                      start: 0,
                      total: 10000,
                    },
                  },
                })
              );
              this.projectsService.changeCurrentPageNumber.next(1);
              this.projectsService.createRequest.next({ type: false, data: action.data });
              if (payload.type === 'submit') {
                this.router
                  .navigate([], {
                    queryParams: {
                      subTab: 'cor',
                      snSubTab: 'workflow',
                    },
                    queryParamsHandling: 'merge',
                  })
                  .then(() => {
                    this.dialog.open(ChangeOrdersRequestReportComponent, {
                      width: '1000px',
                      height: '1100px',
                      data: {
                        ...action.data,
                      },
                    }).afterClosed().subscribe(() => {
                      // Re-fetch CO to pick up status changes from workflow actions (approve/reject)
                      if (action.data?.id) {
                        this.store.dispatch(FetchChangeOrdersRequestWithIdRequest({
                          payload: { changeOrdersRequestId: action.data.id },
                        }));
                      }
                    });
                  });
              }
            } else if (action.type === SaveChangeInWorkRequestFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  sendChangeInWorkRequestReminder$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SendChangeInWorkRequestReminderRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.sendChangeInWorkRequestReminder(payload).pipe(
          map(response => {
            return SendChangeInWorkRequestReminderSuccess({ data: response });
          }),
          catchError((error: any) => {
            return of(SendChangeInWorkRequestReminderFailure({ error }));
          }),
          tap((action: any) => {
            if (action.type === SendChangeInWorkRequestReminderSuccess.type) {
              this.commonService.notification('Reminder sent successfully', 'success');
            } else if (action.type === SendChangeInWorkRequestReminderFailure.type) {
              const errorMessage = action.error?.error?.error || action.error?.message || 'Failed to send reminder';
              this.commonService.errorNotification(errorMessage);
            }
          })
        )
      )
    )
  );
  sendChangeOrdersRequestReminder$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SendChangeOrdersRequestReminderRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.sendChangeOrdersRequestReminder(payload).pipe(
          map(response => {
            return SendChangeOrdersRequestReminderSuccess({ data: response });
          }),
          catchError((error: any) => {
            return of(SendChangeOrdersRequestReminderFailure({ error }));
          }),
          tap((action: any) => {
            if (action.type === SendChangeOrdersRequestReminderSuccess.type) {
              this.commonService.notification('Reminder sent successfully', 'success');
            } else if (action.type === SendChangeOrdersRequestReminderFailure.type) {
              const errorMessage = action.error?.error?.error || action.error?.message || 'Failed to send reminder';
              this.commonService.errorNotification(errorMessage);
            }
          })
        )
      )
    )
  );
  fetchChangeInWorkRequestWithId$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchChangeInWorkRequestWithIdRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchChangeInWorkRequestWithId(payload).pipe(
          map(response => {
            const { data } = response;
            const combinedMedia = [];
            if (data?.pictures && data?.pictures?.length > 0) {
              data?.pictures?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }

                combinedMedia.push(picture);
              });
            }
            if (data?.videos && data?.videos?.length > 0) {
              data?.videos?.forEach(video => {
                video.fileType = 'video';
                combinedMedia.push(video);
              });
            }
            data.media = combinedMedia;
            return FetchChangeInWorkRequestWithIdSuccess({
              changeInWorkRequest: data,
            });
          }),
          catchError(() => {
            return of(FetchChangeInWorkRequestWithIdFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchChangeInWorkRequestWithIdSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchChangeInWorkRequestWithIdFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteCIWR$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteCIWRRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteCIWR(payload).pipe(
          map(() => {
            return DeleteCIWRSuccess({ payload });
          }),
          catchError(() => {
            return of(DeleteCIWRFailure());
          }),
          tap((action: any) => {
            if (action.type === DeleteCIWRSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.closeRightPanel.next();
              this.projectsService.changeCurrentPageNumber.next(1);

              const tempPayload = {
                projectId: action.payload.projectId,
                qp: {
                  start: 0,
                  total: 1000,
                },
              };
              this.store.dispatch(FetchChangeInWorkRequestListRequest({ payload: tempPayload }));

              this.commonService.notification('Change in work order request deleted successfully.', 'success');
            } else if (action.type === DeleteCIWRFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchChangeOrdersRequestWithId$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchChangeOrdersRequestWithIdRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchChangeOrdersRequestWithId(payload).pipe(
          map(response => {
            const { data } = response;
            const combinedMedia = [];
            if (data?.pictures && data?.pictures?.length > 0) {
              data?.pictures?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }

                combinedMedia.push(picture);
              });
            }
            if (data?.videos && data?.videos?.length > 0) {
              data?.videos?.forEach(video => {
                video.fileType = 'video';
                combinedMedia.push(video);
              });
            }
            data.media = combinedMedia;
            return FetchChangeOrdersRequestWithIdSuccess({
              changeOrdersRequest: data,
            });
          }),
          catchError(() => {
            return of(FetchChangeOrdersRequestWithIdFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchChangeOrdersRequestWithIdSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchChangeOrdersRequestWithIdFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  executeWorkflowActionState$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ExecuteWorkflowActionStateRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.executeWorkflowActionState(payload?.payload || payload?.tempPayload).pipe(
          map(response => {
            const { data, message } = response;
            if (data?.attachments && data?.attachments?.length > 0) {
              data?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }
            return ExecuteWorkflowActionStateSuccess({ data, message, payload });
          }),
          catchError(() => {
            return of(ExecuteWorkflowActionStateFailure());
          }),
          tap((action: any) => {
            if (action.type === ExecuteWorkflowActionStateSuccess.type) {
              // Track workflow action
              const wp = action.payload?.payload || action.payload?.tempPayload || action.payload || {};
              this.analyticsService.capture('workflow_action', {
                workflow: wp.workflow || wp.workflowName,
                action: wp.action || wp.actionName,
                project_id: wp.projectId || this.route.snapshot.params?.projectId,
              });
              // Code to execute on API Success Action dispatch
              this.commonService.notification(action.message, 'success');

              if (payload.type !== 'submittals' && payload.type !== 'rfis') {
                const normalizedPayload = action?.payload?.payload || action?.payload?.tempPayload;
                this.store.dispatch(
                  FetchChangeInWorkRequestWithIdRequest({ payload: { changeInWorkRequestId: action?.data?.id } })
                );
                this.store.dispatch(
                  FetchChangeInWorkRequestListRequest({
                    payload: {
                      projectId: normalizedPayload?.projectId,
                      qp: {
                        start: 0,
                        total: 10000,
                      },
                    },
                  })
                );
              }
              if (payload.type === 'COR') {
                // Refresh the change order list + open record so status changes
                // (e.g. submit auto-approving a COR-sourced CO) show without a reload
                const coPayload = action?.payload?.payload || action?.payload?.tempPayload;
                this.store.dispatch(
                  FetchChangeOrdersRequestListRequest({
                    payload: {
                      projectId: coPayload?.projectId,
                      qp: { start: 0, total: 10000 },
                    },
                  })
                );
                if (action?.data?.id) {
                  this.store.dispatch(
                    FetchChangeOrdersRequestWithIdRequest({
                      payload: { changeOrdersRequestId: action.data.id },
                    })
                  );
                }
                this.store.dispatch(
                  FetchLineageRequest({
                    payload: {
                      qp: {
                        start: 0,
                        total: 1000,
                        ex: `change-order-approval/${action?.data?.id}`,
                        include: ['actor', 'state'],
                      },
                    },
                  })
                );
              }
              if (payload.type === 'CIWR') {
                this.store.dispatch(
                  FetchLineageRequest({
                    payload: {
                      qp: {
                        start: 0,
                        total: 1000,
                        ex: `change-order-request-approval/${action?.data?.id}`,
                        include: ['actor', 'state'],
                      },
                    },
                  })
                );
              }
              if (payload.type === 'submittals') {
                this.store.dispatch(
                  FetchSubmittalsItemsListRequest({
                    payload: {
                      start: 0,
                      total: 1000,
                      include: [],
                      projectId: action?.payload?.payload?.projectId || action?.payload?.tempPayload?.projectId,
                    },
                  })
                );
                this.store.dispatch(
                  FetchSubmittalsItemDetailsWithIdRequest({ payload: { submittalsItemId: action?.data?.id } })
                );
                this.projectsService.removeActionFieldsFromForm.next(true);
              }
              if (payload.type === 'rfis') {
                this.store.dispatch(
                  FetchRFIsListRequest({
                    payload: {
                      qp: {
                        start: 0,
                        total: 1000,
                        projectId: action?.payload?.payload?.projectId || action?.payload?.tempPayload?.projectId,
                      },
                    },
                  })
                );
                this.store.dispatch(FetchRFIItemDetailsWithIdRequest({ payload: { rifId: action?.data?.id } }));
                this.projectsService.removeActionFieldsFromForm.next(true);
                this.projectsService.changeView.next(action.data);
              }

              this.projectsService.changeCurrentPageNumber.next(1);

              if (payload.type !== 'submittals' || payload.type !== 'rfis') {
                this.projectsService.closeReportPopup.next(true);
              }
            } else if (action.type === ExecuteWorkflowActionStateFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchLineage$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchLineageRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchLineage(payload).pipe(
          map(response => {
            const { data } = response;
            data.forEach(o => {
              if (o?.attachments && o?.attachments?.length > 0) {
                o?.attachments?.forEach(attachment => {
                  if (attachment?.mime?.includes('image/')) {
                    attachment.fileType = 'picture';
                  } else if (attachment?.mime?.includes('video/')) {
                    attachment.fileType = 'video';
                  } else {
                    attachment.fileType = 'document';
                  }
                });
              }
            });

            const temp = [...data];

            return FetchLineageSuccess({
              lineageList: temp,
            });
          }),
          catchError(() => {
            return of(FetchLineageFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchLineageSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchLineageFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchReport$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchReportRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchReport(payload).pipe(
          map(response => {
            const { html, title } = response;

            return FetchReportSuccess({
              reportInfo: { html, title },
            });
          }),
          catchError(() => {
            return of(FetchReportFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchReportSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchLineageFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  downloadReport$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DownloadReportRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.downloadReport(payload).pipe(
          mergeMap(async (response: any) => {
            const body = response?.body || response;

            // Deferred generation: the API answers 202 + JSON ({jobId, ...})
            // instead of a file — tell the user rather than saving the JSON
            if (response?.status === 202 || (body instanceof Blob && (body.type || '').includes('json'))) {
              let parsed: any = null;
              try {
                parsed = body instanceof Blob ? JSON.parse(await body.text()) : body;
              } catch {
                // Body is not JSON — handled below
              }
              if (parsed?.jobId) {
                this.dialog.open(AlertDialogComponent, {
                  data: {
                    title: 'Report queued',
                    message: parsed.details || 'Too large for direct download. We\'ll email you a download link when it\'s ready.',
                    okLabel: 'OK'
                  },
                  width: '400px',
                  disableClose: false
                });
                return DownloadReportSuccess({});
              }
              // A 202 never carries the report file — without a recognizable
              // queued payload there is nothing valid to save, so surface an
              // error instead of downloading the raw body as the report.
              if (response?.status === 202) {
                this.dialog.open(AlertDialogComponent, {
                  data: {
                    title: 'Download failed',
                    message: parsed?.details || parsed?.message || 'The report could not be downloaded. Please try again.',
                    okLabel: 'OK'
                  },
                  width: '400px',
                  disableClose: false
                });
                this.projectsService.stopLoading.next(true);
                return DownloadReportFailure();
              }
              // Non-202 JSON body without a jobId — fall through and save it
              // (pre-existing behavior for direct downloads).
            }

            // Prefer server-provided filename (Content-Disposition) so org-specific
            // naming from filenameConfig flows through to the downloaded file.
            let filename = `${payload?.reportTitle || 'report'}`;
            const contentDisposition = response?.headers?.get?.('Content-Disposition');
            if (contentDisposition) {
              const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (match && match[1]) {
                filename = match[1].replace(/['"]/g, '').trim();
              }
            }
            saveAs(body, filename);
            return DownloadReportSuccess({});
          }),
          catchError(() => {
            return of(DownloadReportFailure());
          }),
          tap((action: any) => {
            if (action.type === DownloadReportSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.stopLoading.next(true);
            } else if (action.type === DownloadReportFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  deleteDailyReport$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteDailyReportRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteDailyReport(payload).pipe(
          map(response => {
            const { message } = response;
            return DeleteDailyReportSuccess({ message, payload });
          }),
          catchError(e =>
            of(
              DeleteDailyReportFailure({
                error: e?.error?.error || 'Unable to delete daily report',
              })
            )
          ),
          tap((action: any) => {
            if (action.type === DeleteDailyReportSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.notification(action.message, 'success');
              this.store.dispatch(
                FetchDailyReportsByDateRequest({
                  payload: {
                    project: { id: action.payload.projectId },
                    site: action.payload.siteId,
                    qp: {
                      date: action.payload.date,
                      include: ['createdBy', 'statusText', 'site_id', 'next-actions'],
                      total: 1000,
                    },
                  },
                })
              );
              this.commonService.removeReportId.next(true);
            } else if (action.type === DeleteDailyReportFailure.type) {
              // Code to execute on API Failure Action dispatch
              this.commonService.notification(action.error, 'danger');
            }
          })
        )
      )
    )
  );

  createSubmittalsRegistryItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateSubmittalsRegistryItemRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createSubmittalsRegistryItem(payload).pipe(
          map(() => {
            return CreateSubmittalsRegistryItemSuccess({});
          }),
          catchError(() => {
            return of(CreateSubmittalsRegistryItemFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateSubmittalsRegistryItemSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.closeRightPanel.next();
              this.commonService.clearRowSelection.next();

              const tempPayload = {
                start: 0,
                total: 1000,
                include: ['createdBy'],
                projectId: payload?.projectId,
              };

              if (tempPayload?.projectId) {
                this.store.dispatch(FetchSubmittalsRegistryListRequest({ payload: tempPayload }));
              }
            } else if (action.type === CreateSubmittalsRegistryItemFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createSubmittalsItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateSubmittalsItemRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createSubmittalsItem(payload?.payload).pipe(
          map(response => {
            const { data } = response;
            if (data?.attachments && data?.attachments?.length > 0) {
              data?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }
            return CreateSubmittalsItemSuccess({ data, payload });
          }),
          catchError(() => {
            return of(CreateSubmittalsItemFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateSubmittalsItemSuccess.type) {
              // Code to execute on API Success Action dispatch
              const tempPayload = {
                start: 0,
                total: 1000,
                include: [],
                projectId: payload?.payload?.projectId,
              };

              this.store.dispatch(FetchSubmittalsItemsListRequest({ payload: tempPayload }));

              this.projectsService.changeView.next(action?.data);
              this.router.navigate([], {
                queryParams: {
                  tab: 'submittals',
                  subTab: 'list',
                  snSubTab: 'details',
                },
                queryParamsHandling: 'merge',
              });

              if (action.payload.type === 'submit') {
                const pld = {
                  id: action?.data?.id,
                  input: {
                    id: action.data.id,
                  },
                };

                this.store.dispatch(ExecuteSubmitWorkFlowRequest({ payload: pld }));
              }
            } else if (action.type === CreateSubmittalsItemFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  executeSubmitWorkFlow$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ExecuteSubmitWorkFlowRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.executeSubmitWorkFlow(payload).pipe(
          map(response => {
            const { data } = response;
            if (data?.attachments && data?.attachments?.length > 0) {
              data?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }
            return ExecuteSubmitWorkFlowSuccess({ data });
          }),
          catchError(() => {
            return of(ExecuteSubmitWorkFlowFailure());
          }),
          tap((action: any) => {
            if (action.type === ExecuteSubmitWorkFlowSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.changeView.next(action?.data);
              this.store.dispatch(
                FetchSubmittalsItemsListRequest({
                  payload: {
                    start: 0,
                    total: 1000,
                    include: [],
                    projectId: action?.data?.project?.id,
                  },
                })
              );
            } else if (action.type === ExecuteSubmitWorkFlowFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  executeSubmitRFIWorkFlow$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ExecuteSubmitRFIWorkFlowRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.executeSubmitRFIWorkFlow(payload).pipe(
          map(response => {
            const { data } = response;
            if (data?.attachments && data?.attachments?.length > 0) {
              data?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }
            return ExecuteSubmitRFIWorkFlowSuccess({ data });
          }),
          catchError(() => {
            return of(ExecuteSubmitRFIWorkFlowFailure());
          }),
          tap((action: any) => {
            if (action.type === ExecuteSubmitRFIWorkFlowSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.changeView.next(action?.data);
              this.store.dispatch(
                FetchRFIsListRequest({
                  payload: {
                    qp: {
                      start: 0,
                      total: 1000,
                      projectId: action?.data?.project?.id,
                    },
                  },
                })
              );
            } else if (action.type === ExecuteSubmitRFIWorkFlowFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchSubmittalsRegistryList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSubmittalsRegistryListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchSubmittalsRegistryList(payload).pipe(
          map(response => {
            const { data, meta } = response;

            const tempData = data
              .map(o => ({
                ...o,
                heading: [o.section_no, o.sub_section_no].filter(Boolean).join('/'),
                value: o?.id,
                label: o?.title,
              }))
              .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

            return FetchSubmittalsRegistryListSuccess({
              submittalsRegistryListInfo: { list: tempData, totalRecords: meta.totalRecords },
            });
          }),
          catchError(() => {
            return of(FetchSubmittalsRegistryListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchSubmittalsRegistryListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchSubmittalsRegistryListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchSubmittalsItemsList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSubmittalsItemsListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchSubmittalsItemsList(payload).pipe(
          map(response => {
            const { data = [], meta = {} } = response;

            // Step 1: Prepare cleaned data (same as before)
            const cleanedData = data?.map(o => {
              const attachments = (o?.attachments || []).map(picture => ({
                ...picture,
                fileType: picture?.mime.includes('image/') ? 'picture' : 'document',
              }));

              const submittalsLogDraft = `${o?.submittal_log?.section_no ?? ''}/${
                o?.submittal_log?.sub_section_no ?? ''
              }#`;

              return {
                ...o,
                attachments,
                submittalsLogDraft,
              };
            });

            // Step 2: Build a frequency map for all submittalsLogDraft values
            const frequencyMap: Record<string, number> = cleanedData.reduce((acc, curr) => {
              const key = curr.submittalsLogDraft || 'unknown';
              acc[key] = (acc[key] || 0) + 1;
              return acc;
            }, {} as Record<string, number>);

            // Step 3: Attach frequency to each object
            const finalData = cleanedData.map(o => ({
              ...o,
              submittalsLogDraftFrequency: frequencyMap[o.submittalsLogDraft || 'unknown'],
            }));

            // Step 4: Sort by updated_at descending (as before)
            finalData.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

            // Step 5: Dispatch action
            return FetchSubmittalsItemsListSuccess({
              submittalsItemsListInfo: {
                list: finalData,
                totalRecords: meta.totalRecords ?? finalData.length,
              },
            });
          }),
          catchError(() => {
            return of(FetchSubmittalsItemsListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchSubmittalsItemsListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchSubmittalsItemsListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchUsersList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchUsersListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchUsersList(payload).pipe(
          map(response => {
            const { data } = response;

            const tempData = data?.map(o => {
              return {
                ...o,
                label: `${o?.first_name} ${o?.last_name}`,
                value: o?.id,
              };
            });

            return FetchUsersListSuccess({
              usersListInfo: { list: tempData },
            });
          }),
          catchError(() => {
            return of(FetchUsersListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchUsersListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchUsersListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchProjectRolesList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectRolesListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectRolesList(payload).pipe(
          map(response => {
            const { data } = response;

            const tempData = data?.map(o => {
              return {
                ...o,
                label: o?.name || o?.role_name || o?.label,
                value: o?.code || o?.role_code || o?.id || o?.value,
              };
            });

            return FetchProjectRolesListSuccess({
              projectRolesListInfo: { list: tempData },
            });
          }),
          catchError(() => {
            return of(FetchProjectRolesListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchProjectRolesListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchProjectRolesListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchSubmittalsItemDetailsWithId$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSubmittalsItemDetailsWithIdRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchSubmittalsItemDetailsWithId(payload).pipe(
          map(response => {
            const { data } = response;
            if (data?.cc_users?.length) {
              data['cc_users'] = data?.cc_users?.map(o => {
                return {
                  ...o,
                  label: `${o?.first_name} ${o?.last_name}`,
                  value: o?.id,
                };
              });
            }

            if (data?.reviewers?.length) {
              data['reviewers'] = data?.reviewers?.map(o => {
                return {
                  ...o,
                  label: `${o?.first_name} ${o?.last_name}`,
                  value: o?.id,
                };
              });
            }

            if (data?.attachments && data?.attachments?.length > 0) {
              data?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }

            return FetchSubmittalsItemDetailsWithIdSuccess({
              submittalsItemDetails: data,
            });
          }),
          catchError(() => {
            return of(FetchSubmittalsItemDetailsWithIdFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchSubmittalsItemDetailsWithIdSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchSubmittalsItemDetailsWithIdFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteSubmittalsItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteSubmittalsItemRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteSubmittalsItem(payload).pipe(
          map(response => {
            const { message } = response;
            return DeleteSubmittalsItemSuccess({ message, payload });
          }),
          catchError(e => {
            return of(DeleteSubmittalsItemFailure());
          }),
          tap((action: any) => {
            if (action.type === DeleteSubmittalsItemSuccess.type) {
              this.commonService.notification('Submittal deleted successfully', 'success');
              this.projectsService.closeRightPanel.next();
              this.store.dispatch(
                FetchSubmittalsItemsListRequest({
                  payload: {
                    start: 0,
                    total: 1000,
                    include: [],
                    projectId: action.payload.projectId,
                  },
                })
              );
              this.router.navigate([], {
                queryParams: {
                  tab: 'submittals',
                  subTab: 'list',
                  submittalsItemId: null,
                  snSubTab: null,
                },
                queryParamsHandling: 'merge',
              });
            } else if (action.type === DeleteSubmittalsItemFailure.type) {
              this.commonService.notification('Unable to delete submittal', 'danger');
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  submitReviewers$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SubmitReviewersRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.submitReviewers(payload?.tp).pipe(
          map(() => {
            return SubmitReviewersSuccess({ payload });
          }),
          catchError(() => {
            return of(SubmitReviewersFailure());
          }),
          tap((action: any) => {
            if (action.type === SubmitReviewersSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.store.dispatch(ExecuteWorkflowActionStateRequest({ payload: action?.payload?.executePayload }));
            } else if (action.type === SubmitReviewersFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  submitRfiReviewers$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SubmitRfiReviewersRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.submitRfiReviewers(payload?.tp).pipe(
          map(() => {
            return SubmitRfiReviewersSuccess({ payload });
          }),
          catchError(() => {
            return of(SubmitRfiReviewersFailure());
          }),
          tap((action: any) => {
            if (action.type === SubmitRfiReviewersSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.store.dispatch(ExecuteWorkflowActionStateRequest({ payload: action?.payload?.executePayload }));
            } else if (action.type === SubmitRfiReviewersFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  uploadSubmittalLogFile$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UploadSubmittalLogFileRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.uploadSubmittalLogFile(payload).pipe(
          map(() => {
            return UploadSubmittalLogFileSuccess({ payload });
          }),
          catchError(() => {
            return of(UploadSubmittalLogFileFailure({}));
          }),
          tap((action: any) => {
            if (action.type === UploadSubmittalLogFileSuccess.type) {
              // Code to execute on API Success Action dispatch

              this.store.dispatch(
                FetchSubmittalsRegistryListRequest({
                  payload: {
                    start: 0,
                    total: 1000,
                    include: ['createdBy'],
                    projectId: action?.payload?.projectId,
                  },
                })
              );
            } else if (action.type === UploadSubmittalLogFileFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchRFINo$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchRFINoRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchRFINo(payload).pipe(
          map(response => {
            const { data } = response;

            return FetchRFINoSuccess({
              rfiNo: data?.rfi_no,
            });
          }),
          catchError(() => {
            return of(FetchRFINoFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchRFINoSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchRFIsListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchProjectMembersList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectMembersListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectMembersList(payload).pipe(
          map(response => {
            const { data, meta } = response;

            const processedData = (data || [])
              .map((member: any) => {
                if (member?.sites && Array.isArray(member.sites)) {
                  return {
                    ...member,
                    sites: member.sites.map((site: any) => ({
                      ...site,
                      label: site?.name || site?.label,
                      value: site?.id || site?.value,
                    })),
                  };
                }
                return member;
              })
              .sort((a, b) => {
                const dateA = a?.added_at ? new Date(a.added_at).getTime() : 0;
                const dateB = b?.added_at ? new Date(b.added_at).getTime() : 0;
                return dateB - dateA;
              });

            return FetchProjectMembersListSuccess({
              projectMembersListInfo: {
                list: processedData,
                totalRecords: meta?.totalRecords || processedData?.length || 0,
              },
            });
          }),
          catchError(() => {
            return of(FetchProjectMembersListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchProjectMembersListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchProjectMembersListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchContractorsList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchContractorsListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchContractorsList(payload).pipe(
          map(response => {
            const { data, meta } = response;

            const processedData = (data || []) || [];

            return FetchContractorsListSuccess({
              contractorsListInfo: {
                list: processedData,
                totalRecords: meta?.totalRecords || processedData?.length || 0,
              },
            });
          }),
          catchError(() => {
            return of(FetchContractorsListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchContractorsListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchContractorsListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchSitesList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSitesListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchSitesList(payload).pipe(
          map(response => {
            const { data, meta } = response;

            const processedData = (data || []).map((site: any) => {
              const duration = site.duration || 0;
              const durationUsed = site.duration_used || 0;
              const progress = duration > 0 ? Math.round((durationUsed / duration) * 100) : 0;

              return {
                ...site,
                progress,
                progressText: `${durationUsed} / ${duration} days`,
              };
            });

            processedData.sort((a, b) => {
              const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
              const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
              return dateB - dateA;
            });

            return FetchSitesListSuccess({
              sitesListInfo: {
                list: processedData,
                totalRecords: meta?.totalRecords || processedData?.length || 0,
              },
            });
          }),
          catchError(() => {
            return of(FetchSitesListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchSitesListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchSitesListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createContractor$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateContractorRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createContractor(payload).pipe(
          map(response => {
            this.commonService.notification('Contractor created successfully', 'success');
            return CreateContractorSuccess({ data: response?.data, payload });
          }),
          catchError(() => {
            this.commonService.notification('Failed to create contractor', 'error');
            return of(CreateContractorFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateContractorSuccess.type) {
              this.projectsService.closeRightPanel.next();
              if (action?.payload?.projectId) {
                this.store.dispatch(
                  FetchContractorsListRequest({
                    payload: {
                      projectId: action?.payload?.projectId,
                    },
                  })
                );
              }
            } else if (action.type === CreateContractorFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateContractor$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateContractorRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateContractor(payload).pipe(
          map(response => {
            this.commonService.notification('Contractor updated successfully', 'success');
            return UpdateContractorSuccess({ data: response?.data, payload });
          }),
          catchError(() => {
            this.commonService.notification('Failed to update contractor', 'error');
            return of(UpdateContractorFailure());
          }),
          tap((action: any) => {
            if (action.type === UpdateContractorSuccess.type) {
              this.projectsService.closeRightPanel.next();
              if (action?.payload?.projectId) {
                this.store.dispatch(
                  FetchContractorsListRequest({
                    payload: {
                      projectId: action?.payload?.projectId,
                    },
                  })
                );
              }
            } else if (action.type === UpdateContractorFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteContractor$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteContractorRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteContractor(payload).pipe(
          map(response => {
            this.commonService.notification('Contractor deleted successfully', 'success');
            return DeleteContractorSuccess({ data: response?.data, payload });
          }),
          catchError(() => {
            this.commonService.notification('Failed to delete contractor', 'error');
            return of(DeleteContractorFailure());
          }),
          tap((action: any) => {
            if (action.type === DeleteContractorSuccess.type) {
              this.projectsService.closeRightPanel.next();
              if (action?.payload?.projectId) {
                this.store.dispatch(
                  FetchContractorsListRequest({
                    payload: {
                      projectId: action?.payload?.projectId,
                    },
                  })
                );
              }
            } else if (action.type === DeleteContractorFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchDeliveryTrackingFields$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchDeliveryTrackingFieldsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchDeliveryTrackingFields(payload).pipe(
          map(response => {
            const data = response?.data ?? response;
            const list = Array.isArray(data) ? data : [];
            return FetchDeliveryTrackingFieldsSuccess({
              deliveryTrackingFieldsListInfo: {
                list,
                totalRecords: list.length,
              },
            });
          }),
          catchError(() => of(FetchDeliveryTrackingFieldsFailure())),
          tap((action: any) => {
            if (action.type === FetchDeliveryTrackingFieldsFailure.type) {
              this.commonService.notification('Failed to load delivery tracking fields', 'error');
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createDeliveryTrackingField$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateDeliveryTrackingFieldRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createDeliveryTrackingField(payload).pipe(
          map(response => {
            const data = response?.data ?? response;
            return CreateDeliveryTrackingFieldSuccess({ data, payload });
          }),
          catchError(() => {
            this.commonService.notification('Failed to create field', 'error');
            return of(CreateDeliveryTrackingFieldFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateDeliveryTrackingFieldSuccess.type) {
              this.commonService.notification('Field created successfully', 'success');
              this.projectsService.closeRightPanel.next();
              if (action?.payload?.projectId) {
                this.store.dispatch(
                  FetchDeliveryTrackingFieldsRequest({
                    payload: { projectId: action.payload.projectId },
                  })
                );
              }
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateDeliveryTrackingField$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateDeliveryTrackingFieldRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateDeliveryTrackingField(payload).pipe(
          map(response => {
            const data = response?.data ?? response;
            return UpdateDeliveryTrackingFieldSuccess({ data, payload });
          }),
          catchError(() => {
            this.commonService.notification('Failed to update field', 'error');
            return of(UpdateDeliveryTrackingFieldFailure());
          }),
          tap((action: any) => {
            if (action.type === UpdateDeliveryTrackingFieldSuccess.type) {
              this.commonService.notification('Field updated successfully', 'success');
              this.projectsService.closeRightPanel.next();
              if (action?.payload?.projectId) {
                this.store.dispatch(
                  FetchDeliveryTrackingFieldsRequest({
                    payload: { projectId: action.payload.projectId },
                  })
                );
              }
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteDeliveryTrackingField$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteDeliveryTrackingFieldRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload => {
        if (!payload?.field?.id) {
          this.commonService.stopLoading();
          return of(DeleteDeliveryTrackingFieldFailure());
        }
        return this.projectsService.deleteDeliveryTrackingField(payload).pipe(
          map(() =>
            DeleteDeliveryTrackingFieldSuccess({
              data: null,
              payload: { projectId: payload?.projectId },
            })
          ),
          catchError(() => {
            this.commonService.notification('Failed to delete field', 'error');
            return of(DeleteDeliveryTrackingFieldFailure());
          }),
          tap((action: any) => {
            if (action.type === DeleteDeliveryTrackingFieldSuccess.type) {
              this.commonService.notification('Field deleted successfully', 'success');
              this.projectsService.closeRightPanel.next();
              if (action?.payload?.projectId) {
                this.store.dispatch(
                  FetchDeliveryTrackingFieldsRequest({
                    payload: { projectId: action.payload.projectId },
                  })
                );
              }
            }
            this.commonService.stopLoading();
          })
        );
      })
    )
  );

  deleteSite$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteSiteRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteSite(payload).pipe(
          map(response => {
            this.commonService.notification('Site deleted successfully', 'success');
            return DeleteSiteSuccess({ data: response?.data, payload });
          }),
          catchError(() => {
            this.commonService.notification('Failed to delete site', 'error');
            return of(DeleteSiteFailure());
          }),
          tap((action: any) => {
            if (action.type === DeleteSiteSuccess.type) {
              if (action?.payload?.projectId) {
                this.store.dispatch(
                  FetchSitesListRequest({
                    payload: {
                      projectId: action?.payload?.projectId,
                      qp: {
                        start: 0,
                        total: 1000,
                      },
                    },
                  })
                );
              }
            } else if (action.type === DeleteSiteFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createSite$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateSiteRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createSite(payload).pipe(
          map(response => {
            this.commonService.notification('Site created successfully', 'success');
            return CreateSiteSuccess({ data: response?.data, payload });
          }),
          catchError(() => {
            this.commonService.notification('Failed to create site', 'error');
            return of(CreateSiteFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateSiteSuccess.type) {
              this.projectsService.closeRightPanel.next();
              if (action?.payload?.project_id) {
                this.store.dispatch(
                  FetchSitesListRequest({
                    payload: {
                      projectId: action?.payload?.project_id,
                      qp: {
                        start: 0,
                        total: 1000,
                      },
                    },
                  })
                );
              }
            } else if (action.type === CreateSiteFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateSite$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateSiteRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateSite(payload).pipe(
          map(response => {
            this.commonService.notification('Site updated successfully', 'success');
            return UpdateSiteSuccess({ data: response?.data, payload });
          }),
          catchError(() => {
            this.commonService.notification('Failed to update site', 'error');
            return of(UpdateSiteFailure());
          }),
          tap((action: any) => {
            if (action.type === UpdateSiteSuccess.type) {
              this.projectsService.closeRightPanel.next();
              if (action?.payload?.project_id) {
                this.store.dispatch(
                  FetchSitesListRequest({
                    payload: {
                      projectId: action?.payload?.project_id,
                      qp: {
                        start: 0,
                        total: 1000,
                      },
                    },
                  })
                );
              }
            } else if (action.type === UpdateSiteFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchExistingMembersList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchExistingMembersListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchExistingMembersList(payload).pipe(
          map(response => {
            const { data } = response;

            const tempData = data?.map(o => {
              return {
                ...o,
                label: `${o?.first_name || ''} ${o?.last_name || ''}`.trim(),
                value: o?.uuid || o?.id,
              };
            });

            return FetchExistingMembersListSuccess({
              existingMembersListInfo: { list: tempData },
            });
          }),
          catchError(() => {
            return of(FetchExistingMembersListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchExistingMembersListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchExistingMembersListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  inviteUser$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(InviteUserRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.inviteUser(payload).pipe(
          map(response => {
            this.commonService.notification('User invited successfully', 'success');
            return InviteUserSuccess({ data: response?.data, payload });
          }),
          catchError((err) => {
            const error = typeof err === 'function' ? err() : err;
            let errorMessage = 'Failed to invite user';
            const errorBody = error?.error;
            if (typeof errorBody?.error === 'string') {
              errorMessage = errorBody.error;
            } else if (typeof errorBody?.error === 'object') {
              // Handle object format: { email: { msg: "..." }, phone: { msg: "..." } }
              const messages = Object.values(errorBody.error)
                .map((field: any) => field?.msg)
                .filter(Boolean);
              if (messages.length) {
                errorMessage = messages.join(', ');
              }
            } else if (errorBody?.message) {
              errorMessage = errorBody.message;
            }
            this.commonService.notification(errorMessage, 'error');
            return of(InviteUserFailure());
          }),
          tap((action: any) => {
            if (action.type === InviteUserSuccess.type) {
              this.projectsService.closeRightPanel.next();
              if (action?.payload?.projectId) {
                this.store.dispatch(
                  FetchProjectMembersListRequest({
                    payload: {
                      projectId: action?.payload?.projectId,
                    },
                  })
                );
              }
            } else if (action.type === InviteUserFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateUser$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateUserRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateUser(payload).pipe(
          map(response => {
            this.commonService.notification('User updated successfully', 'success');
            return UpdateUserSuccess({ data: response?.data, payload });
          }),
          catchError((err) => {
            const error = typeof err === 'function' ? err() : err;
            let errorMessage = 'Failed to update user';
            const errorBody = error?.error;
            if (typeof errorBody?.error === 'string') {
              errorMessage = errorBody.error;
            } else if (typeof errorBody?.error === 'object') {
              const messages = Object.values(errorBody.error)
                .map((field: any) => field?.msg)
                .filter(Boolean);
              if (messages.length) {
                errorMessage = messages.join(', ');
              }
            } else if (errorBody?.message) {
              errorMessage = errorBody.message;
            }
            this.commonService.notification(errorMessage, 'error');
            return of(UpdateUserFailure());
          }),
          tap((action: any) => {
            if (action.type === UpdateUserSuccess.type) {
              this.projectsService.closeRightPanel.next();
              if (action?.payload?.projectId) {
                this.store.dispatch(
                  FetchProjectMembersListRequest({
                    payload: {
                      projectId: action?.payload?.projectId,
                    },
                  })
                );
              }
            } else if (action.type === UpdateUserFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateProject$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateProjectRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return {
          payload: action.payload,
          successMessage: action.successMessage,
        };
      }),
      mergeMap(({ payload, successMessage }) =>
        this.projectsService.updateProject(payload, { include: ['cover_photo', 'address'] }).pipe(
          map((response: any) => {
            const data = response?.data ?? response;
            this.commonService.notification(successMessage ?? `Project "${data?.name}" saved`, 'success');
            return UpdateProjectSuccess({ data, payload });
          }),
          catchError((err) => {
            this.commonService.notification(this.getApiErrorMessage(err, 'Failed to save project'), 'danger');
            return of(UpdateProjectFailure());
          }),
          tap((action: any) => {
            if (action.type === UpdateProjectSuccess.type) {
              this.projectsService.isAnyFormDirty.next(false);
              this.projectsService.closeRightPanel.next();
              this.store.dispatch(
                FetchProjectsListRequest({
                  payload: {
                    qp: {
                      start: 0,
                      total: 1000,
                      all: 'all',
                      include: [
                        'sites',
                        'cover_photo',
                        'status',
                        'summary',
                        'online',
                        'material_types',
                        'gauge_numbers',
                        'material_sources',
                      ],
                    },
                  },
                }),
              );
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteUser$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteUserRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteUser(payload).pipe(
          map(response => {
            this.commonService.notification('User deleted successfully', 'success');
            return DeleteUserSuccess({ data: response?.data, payload });
          }),
          catchError(() => {
            this.commonService.notification('Failed to delete user', 'error');
            return of(DeleteUserFailure());
          }),
          tap((action: any) => {
            if (action.type === DeleteUserSuccess.type) {
              this.projectsService.closeRightPanel.next();
              if (action?.payload?.projectId) {
                this.store.dispatch(
                  FetchProjectMembersListRequest({
                    payload: {
                      projectId: action?.payload?.projectId,
                    },
                  })
                );
              }
            } else if (action.type === DeleteUserFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchRFIsList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchRFIsListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchRFIsList(payload).pipe(
          map(response => {
            const { data, meta } = response;

            return FetchRFIsListSuccess({
              rfisListInfo: {
                list: data,
                totalRecords: meta.totalRecords,
              },
            });
          }),
          catchError(() => {
            return of(FetchRFIsListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchRFIsListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchRFIsListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createRFI$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateRFIRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createRFI(payload?.payload).pipe(
          map(response => {
            void this.sheetMarkups.flush(response);
            const { data } = response;
            if (data?.attachments && data?.attachments?.length > 0) {
              data?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }
            return CreateRFISuccess({ data, payload });
          }),
          catchError(err => {
            this.commonService.notification(this.getApiErrorMessage(err, 'Failed to save RFI.'), 'error');
            return of(CreateRFIFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateRFISuccess.type) {
              // Code to execute on API Success Action dispatch

              this.store.dispatch(
                FetchRFIsListRequest({
                  payload: {
                    qp: {
                      start: 0,
                      total: 1000,
                      projectId: action?.data?.project?.id,
                    },
                  },
                })
              );

              afterRfiCreated(action.payload, action?.data, {
                closePanel: () => this.projectsService.closeRightPanel.next(),
                showRfi: data => this.projectsService.changeView.next(data),
                navigate: queryParams => this.router.navigate([], { queryParams, queryParamsHandling: 'merge' }),
              });

              if (action.payload.type === 'submit') {
                const pld = {
                  id: action?.data?.id,
                  input: {
                    formValues: '',
                    id: action.data.id,
                  },
                };

                this.store.dispatch(ExecuteSubmitRFIWorkFlowRequest({ payload: pld }));
              }
            } else if (action.type === CreateRFIFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchRFIItemDetailsWithId$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchRFIItemDetailsWithIdRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchRFIItemDetailsWithId(payload).pipe(
          map(response => {
            const { data } = response;
            if (data?.cc_users?.length) {
              data['cc_users'] = data?.cc_users?.map(o => {
                return {
                  ...o,
                  label: `${o?.first_name} ${o?.last_name}`,
                  value: o?.id,
                };
              });
            }
            if (data?.reviewers?.length) {
              data['reviewers'] = data?.reviewers?.map(o => {
                return {
                  ...o,
                  label: `${o?.first_name} ${o?.last_name}`,
                  value: o?.id,
                };
              });
            }
            if (data?.attachments && data?.attachments?.length > 0) {
              data?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }

            return FetchRFIItemDetailsWithIdSuccess({
              rfiItemDetails: data,
            });
          }),
          catchError(() => {
            return of(FetchRFIItemDetailsWithIdFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchRFIItemDetailsWithIdSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchRFIItemDetailsWithIdFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchLaborLookup$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchLaborLookupRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectLookup({ ...payload, type: 'labor' }).pipe(
          map(response => {
            const { data, meta } = response;
            return FetchLaborLookupSuccess({
              laborLookupData: {
                list: data || [],
                totalRecords: meta?.totalRecords || data?.length || 0,
              },
            });
          }),
          catchError(() => {
            return of(FetchLaborLookupFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchLaborLookupSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchLaborLookupFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createLaborLookup$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateLaborLookupRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createProjectLookup({ ...payload, type: 'labor' }).pipe(
          map(response => {
            const { data } = response;
            return CreateLaborLookupSuccess({
              data,
              projectId: payload.projectId,
            });
          }),
          catchError(() => {
            return of(CreateLaborLookupFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateLaborLookupSuccess.type) {
              // Code to execute on API Success Action dispatch
              // Refresh the lookup list after creating
              this.store.dispatch(
                FetchLaborLookupRequest({
                  payload: {
                    projectId: action.projectId,
                    qp: {
                      start: 0,
                      total: 1000,
                    },
                  },
                })
              );
            } else if (action.type === CreateLaborLookupFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchEquipmentLookup$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchEquipmentLookupRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectLookup({ ...payload, type: 'equipment' }).pipe(
          map(response => {
            const { data, meta } = response;
            return FetchEquipmentLookupSuccess({
              equipmentLookupData: {
                list: data || [],
                totalRecords: meta?.totalRecords || data?.length || 0,
              },
            });
          }),
          catchError(() => {
            return of(FetchEquipmentLookupFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchEquipmentLookupSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchEquipmentLookupFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createEquipmentLookup$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateEquipmentLookupRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createProjectLookup({ ...payload, type: 'equipment' }).pipe(
          map(response => {
            const { data } = response;
            return CreateEquipmentLookupSuccess({
              data,
              projectId: payload.projectId,
            });
          }),
          catchError(() => {
            return of(CreateEquipmentLookupFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateEquipmentLookupSuccess.type) {
              // Code to execute on API Success Action dispatch
              // Refresh the lookup list after creating
              this.store.dispatch(
                FetchEquipmentLookupRequest({
                  payload: {
                    projectId: action.projectId,
                    qp: {
                      start: 0,
                      total: 1000,
                    },
                  },
                })
              );
            } else if (action.type === CreateEquipmentLookupFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchActivityTypeLookup$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchActivityTypeLookupRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectLookup({ ...payload, type: 'activity_type' }).pipe(
          map(response => {
            const { data, meta } = response;
            return FetchActivityTypeLookupSuccess({
              activityTypeLookupData: {
                list: data || [],
                totalRecords: meta?.totalRecords || data?.length || 0,
              },
            });
          }),
          catchError(() => {
            return of(FetchActivityTypeLookupFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchActivityTypeLookupSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchActivityTypeLookupFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createActivityTypeLookup$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateActivityTypeLookupRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createProjectLookup({ ...payload, type: 'activity_type' }).pipe(
          map(response => {
            const { data } = response;
            return CreateActivityTypeLookupSuccess({
              data,
              projectId: payload.projectId,
            });
          }),
          catchError(() => {
            return of(CreateActivityTypeLookupFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateActivityTypeLookupSuccess.type) {
              // Code to execute on API Success Action dispatch
              // Refresh the lookup list after creating
              this.store.dispatch(
                FetchActivityTypeLookupRequest({
                  payload: {
                    projectId: action.projectId,
                    qp: {
                      start: 0,
                      total: 1000,
                    },
                  },
                })
              );
            } else if (action.type === CreateActivityTypeLookupFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteLaborLookup$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteLaborLookupRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteProjectLookup({ ...payload, type: 'labor' }).pipe(
          map(response => {
            const { message } = response;
            return DeleteLaborLookupSuccess({
              message,
              projectId: payload.projectId,
            });
          }),
          catchError(() => {
            return of(DeleteLaborLookupFailure());
          }),
          tap((action: any) => {
            if (action.type === DeleteLaborLookupSuccess.type) {
              this.commonService.notification(action.message, 'success');
              this.store.dispatch(
                FetchLaborLookupRequest({
                  payload: {
                    projectId: action.projectId,
                    qp: {
                      start: 0,
                      total: 1000,
                    },
                  },
                })
              );
            } else if (action.type === DeleteLaborLookupFailure.type) {
              this.commonService.notification('Unable to delete labor lookup.', 'danger');
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteEquipmentLookup$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteEquipmentLookupRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteProjectLookup({ ...payload, type: 'equipment' }).pipe(
          map(response => {
            const { message } = response;
            return DeleteEquipmentLookupSuccess({
              message,
              projectId: payload.projectId,
            });
          }),
          catchError(() => {
            return of(DeleteEquipmentLookupFailure());
          }),
          tap((action: any) => {
            if (action.type === DeleteEquipmentLookupSuccess.type) {
              this.commonService.notification(action.message, 'success');
              this.store.dispatch(
                FetchEquipmentLookupRequest({
                  payload: {
                    projectId: action.projectId,
                    qp: {
                      start: 0,
                      total: 1000,
                    },
                  },
                })
              );
            } else if (action.type === DeleteEquipmentLookupFailure.type) {
              this.commonService.notification('Unable to delete equipment lookup.', 'danger');
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteActivityTypeLookup$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteActivityTypeLookupRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteProjectLookup({ ...payload, type: 'activity_type' }).pipe(
          map(response => {
            const { message } = response;
            return DeleteActivityTypeLookupSuccess({
              message,
              projectId: payload.projectId,
            });
          }),
          catchError(() => {
            return of(DeleteActivityTypeLookupFailure());
          }),
          tap((action: any) => {
            if (action.type === DeleteActivityTypeLookupSuccess.type) {
              this.commonService.notification(action.message, 'success');
              this.store.dispatch(
                FetchActivityTypeLookupRequest({
                  payload: {
                    projectId: action.projectId,
                    qp: {
                      start: 0,
                      total: 1000,
                    },
                  },
                })
              );
            } else if (action.type === DeleteActivityTypeLookupFailure.type) {
              this.commonService.notification('Unable to delete activity type lookup.', 'danger');
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchExternalLabLookup$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchExternalLabLookupRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectLookup({ ...payload, type: 'external_lab' }).pipe(
          map(response => {
            const { data, meta } = response;
            return FetchExternalLabLookupSuccess({
              externalLabLookupData: {
                list: data || [],
                totalRecords: meta?.totalRecords || data?.length || 0,
              },
            });
          }),
          catchError(() => {
            return of(FetchExternalLabLookupFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchExternalLabLookupSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchExternalLabLookupFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchMaterialTestLabList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchMaterialTestLabListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchMaterialTestLabList(payload).pipe(
          map(response => {
            const { data, meta } = response;
            return FetchMaterialTestLabListSuccess({
              externalLabListInfo: {
                list: data || [],
                totalRecords: meta?.totalRecords || data?.length || 0,
              },
            });
          }),
          catchError(() => {
            return of(FetchMaterialTestLabListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchMaterialTestLabListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchMaterialTestLabListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createExternalLab$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateExternalLabRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createMaterialTestLab(payload).pipe(
          map(response => {
            const { data } = response;
            this.commonService.notification('External lab created successfully', 'success');
            return CreateExternalLabSuccess({
              data,
              projectId: payload.projectId,
            });
          }),
          catchError(() => {
            this.commonService.notification('Failed to create external lab', 'error');
            return of(CreateExternalLabFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateExternalLabSuccess.type) {
              this.projectsService.closeRightPanel.next();
              this.store.dispatch(
                FetchMaterialTestLabListRequest({
                  payload: { projectId: action.projectId },
                })
              );
            } else if (action.type === CreateExternalLabFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateExternalLab$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateExternalLabRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateMaterialTestLab(payload).pipe(
          map(response => {
            const { data } = response;
            this.commonService.notification('External lab updated successfully', 'success');
            return UpdateExternalLabSuccess({
              data,
              projectId: payload.projectId,
            });
          }),
          catchError(() => {
            this.commonService.notification('Failed to update external lab', 'error');
            return of(UpdateExternalLabFailure());
          }),
          tap((action: any) => {
            if (action.type === UpdateExternalLabSuccess.type) {
              this.projectsService.closeRightPanel.next();
              this.store.dispatch(
                FetchMaterialTestLabListRequest({
                  payload: { projectId: action.projectId },
                })
              );
            } else if (action.type === UpdateExternalLabFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  deleteExternalLabLookup$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteExternalLabLookupRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteProjectLookup({ ...payload, type: 'external_lab' }).pipe(
          map(response => {
            const { message } = response;
            return DeleteExternalLabLookupSuccess({
              message,
              projectId: payload.projectId,
            });
          }),
          catchError(() => {
            return of(DeleteExternalLabLookupFailure());
          }),
          tap((action: any) => {
            if (action.type === DeleteExternalLabLookupSuccess.type) {
              this.commonService.notification(action.message, 'success');
              this.store.dispatch(
                FetchMaterialTestLabListRequest({
                  payload: { projectId: action.projectId },
                })
              );
            } else if (action.type === DeleteExternalLabLookupFailure.type) {
              this.commonService.notification('Unable to delete external lab lookup.', 'danger');
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchMaterialTestNameList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchMaterialTestNameListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchMaterialTestNameList(payload).pipe(
          map(response => {
            const { data } = response;
            const tempData = data?.map(o => {
              return {
                ...o,
                label: o.name,
                value: o.id,
              };
            });
            return FetchMaterialTestNameListSuccess({
              list: tempData,
            });
          }),
          catchError(() => {
            return of(FetchMaterialTestNameListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchMaterialTestNameListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchMaterialTestNameListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchProjectMaterialTestList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectMaterialTestListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload?.payload || action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchProjectMaterialTestList(payload).pipe(
          map(response => {
            const { data } = response;
            const list = Array.isArray(data) ? data : [];
            return FetchProjectMaterialTestListSuccess({
              projectMaterialTestListInfo: {
                list,
                totalRecords: list.length,
              },
            });
          }),
          catchError(() => {
            return of(FetchProjectMaterialTestListFailure());
          }),
          tap(() => {
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createProjectMaterialTest$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateProjectMaterialTestRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService.createProjectMaterialTest({ projectId: action.projectId, name: action.name }).pipe(
          map(() =>
            CreateProjectMaterialTestSuccess({
              projectId: action.projectId,
            })
          ),
          catchError(() => of(CreateProjectMaterialTestFailure())),
          tap((resultAction: any) => {
            if (resultAction.type === CreateProjectMaterialTestSuccess.type) {
              this.dialog.closeAll();
              this.commonService.notification('Material test created successfully.', 'success');
              this.store.dispatch(
                FetchProjectMaterialTestListRequest({
                  payload: {
                    projectId: resultAction.projectId,
                    qp: { start: 0, total: 1000 },
                  },
                })
              );
            } else if (resultAction.type === CreateProjectMaterialTestFailure.type) {
              this.commonService.notification('Unable to create material test.', 'danger');
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateProjectMaterialTest$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateProjectMaterialTestRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService
          .updateProjectMaterialTest({
            projectId: action.projectId,
            testId: action.testId,
            name: action.name,
          })
          .pipe(
            map(() =>
              UpdateProjectMaterialTestSuccess({
                projectId: action.projectId,
              })
            ),
            catchError(() => of(UpdateProjectMaterialTestFailure())),
            tap((resultAction: any) => {
              if (resultAction.type === UpdateProjectMaterialTestSuccess.type) {
                this.dialog.closeAll();
                this.commonService.notification('Material test updated successfully.', 'success');
                this.store.dispatch(
                  FetchProjectMaterialTestListRequest({
                    payload: {
                      projectId: resultAction.projectId,
                      qp: { start: 0, total: 1000 },
                    },
                  })
                );
              } else if (resultAction.type === UpdateProjectMaterialTestFailure.type) {
                this.commonService.notification('Unable to update material test.', 'danger');
              }
              this.commonService.stopLoading();
            })
          )
      )
    )
  );

  deleteProjectMaterialTest$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteProjectMaterialTestRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((action: any) =>
        this.projectsService.deleteProjectMaterialTest({ projectId: action.projectId, testId: action.testId }).pipe(
          map(() =>
            DeleteProjectMaterialTestSuccess({
              projectId: action.projectId,
            })
          ),
          catchError(() => of(DeleteProjectMaterialTestFailure())),
          tap((resultAction: any) => {
            if (resultAction.type === DeleteProjectMaterialTestSuccess.type) {
              this.commonService.notification('Material test deleted successfully.', 'success');
              this.store.dispatch(
                FetchProjectMaterialTestListRequest({
                  payload: {
                    projectId: resultAction.projectId,
                    qp: { start: 0, total: 1000 },
                  },
                })
              );
            } else if (resultAction.type === DeleteProjectMaterialTestFailure.type) {
              this.commonService.notification('Unable to delete material test.', 'danger');
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchLabNameList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchLabNameListRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchLabNameList(payload).pipe(
          map(response => {
            const { data } = response;
            const tempData = data?.map(o => {
              return {
                ...o,
                label: o.name,
                value: o.id,
              };
            });
            return FetchLabNameListSuccess({
              list: tempData,
            });
          }),
          catchError(() => {
            return of(FetchLabNameListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchLabNameListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchLabNameListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  saveExternalTest$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SaveExternalTestRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.saveExternalTest(payload).pipe(
          map(response => {
            const { data } = response;
            if (data?.pictures?.length) {
              data?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }
            return SaveExternalTestSuccess({ data });
          }),
          catchError(() => {
            return of(SaveExternalTestFailure());
          }),
          tap((action: any) => {
            if (action.type === SaveExternalTestSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.store.dispatch(SendExternalTestToLabRequest({ payload: { testId: action?.data?.id } }));
            } else if (action.type === SaveExternalTestFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  sendExternalTestToLab$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SendExternalTestToLabRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.sendExternalTestToLab(payload).pipe(
          map(response => {
            const { data } = response;
            if (data?.pictures?.length) {
              data?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }

            if (data?.inspection_detail?.pictures?.length) {
              data?.inspection_detail?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }

            if (data?.material_test_report?.attachments?.length) {
              data?.material_test_report?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }
            return SendExternalTestToLabSuccess({ data });
          }),
          catchError(() => {
            return of(SendExternalTestToLabFailure());
          }),
          tap((action: any) => {
            if (action.type === SendExternalTestToLabSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.isAnyFormDirty.next(false);
              this.projectsService.closeRightPanel.next();
              this.dailyReportService.fetchDailyReportById.next({});
            } else if (action.type === SendExternalTestToLabFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  acceptExternalTestLabSchedule$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(AcceptExternalTestLabScheduleRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.acceptExternalTestLabSchedule(payload).pipe(
          map(response => {
            const { data } = response;
            if (data?.pictures?.length) {
              data?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }

            if (data?.inspection_detail?.pictures?.length) {
              data?.inspection_detail?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }

            if (data?.material_test_report?.attachments?.length) {
              data?.material_test_report?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }
            return AcceptExternalTestLabScheduleSuccess({ data });
          }),
          catchError(() => {
            return of(AcceptExternalTestLabScheduleFailure());
          }),
          tap((action: any) => {
            if (action.type === AcceptExternalTestLabScheduleSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.isAnyFormDirty.next(false);
              this.projectsService.changeView.next(action.data);
            } else if (action.type === AcceptExternalTestLabScheduleFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  recordExternalTestLabTestDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(RecordExternalTestLabTestDetailsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.recordExternalTestLabTestDetails(payload).pipe(
          map(response => {
            const { data } = response;
            if (data?.pictures?.length) {
              data?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }

            if (data?.inspection_detail?.pictures?.length) {
              data?.inspection_detail?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }

            if (data?.material_test_report?.attachments?.length) {
              data?.material_test_report?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }
            return RecordExternalTestLabTestDetailsSuccess({ data });
          }),
          catchError(() => {
            return of(RecordExternalTestLabTestDetailsFailure());
          }),
          tap((action: any) => {
            if (action.type === RecordExternalTestLabTestDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.isAnyFormDirty.next(false);
              this.projectsService.changeView.next(action.data);
            } else if (action.type === RecordExternalTestLabTestDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateExternalTestLabTestReport$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateExternalTestLabTestReportRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateExternalTestLabTestReport(payload).pipe(
          map(response => {
            const { data } = response;
            if (data?.pictures?.length) {
              data?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }

            if (data?.inspection_detail?.pictures?.length) {
              data?.inspection_detail?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }

            if (data?.material_test_report?.attachments?.length) {
              data?.material_test_report?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }
            return UpdateExternalTestLabTestReportSuccess({ data });
          }),
          catchError(() => {
            return of(UpdateExternalTestLabTestReportFailure());
          }),
          tap((action: any) => {
            if (action.type === UpdateExternalTestLabTestReportSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.isAnyFormDirty.next(false);
              this.projectsService.changeView.next(action.data);
            } else if (action.type === UpdateExternalTestLabTestReportFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  updateExternalTestLabTestResult$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateExternalTestLabTestResultRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateExternalTestLabTestResult(payload).pipe(
          map(response => {
            const { data } = response;
            if (data?.pictures?.length) {
              data?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }
            if (data?.inspection_detail?.pictures?.length) {
              data?.inspection_detail?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }

            if (data?.material_test_report?.attachments?.length) {
              data?.material_test_report?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }
            return UpdateExternalTestLabTestResultSuccess({ data });
          }),
          catchError(() => {
            return of(UpdateExternalTestLabTestResultFailure());
          }),
          tap((action: any) => {
            if (action.type === UpdateExternalTestLabTestResultSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.projectsService.isAnyFormDirty.next(false);
              this.projectsService.changeView.next(action.data);
            } else if (action.type === UpdateExternalTestLabTestResultFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  cancelExternalTest$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CancelExternalTestRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.cancelExternalTest(payload).pipe(
          map(response => {
            const { data } = response;
            if (data?.pictures?.length) {
              data?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }

            if (data?.inspection_detail?.pictures?.length) {
              data?.inspection_detail?.pictures?.forEach(p => {
                if (p?.mime.includes('image/')) {
                  p.fileType = 'picture';
                } else {
                  p.fileType = 'document';
                }
              });
            }

            if (data?.material_test_report?.attachments?.length) {
              data?.material_test_report?.attachments?.forEach(picture => {
                if (picture?.mime.includes('image/')) {
                  picture.fileType = 'picture';
                } else {
                  picture.fileType = 'document';
                }
              });
            }
            return CancelExternalTestSuccess({ data });
          }),
          catchError(() => {
            return of(CancelExternalTestFailure());
          }),
          tap((action: any) => {
            if (action.type === CancelExternalTestSuccess.type) {
              this.projectsService.isAnyFormDirty.next(false);
              this.projectsService.closeRightPanel.next();
              this.siteMaterialTestService.fetchSiteMaterialTests.next(null);
            } else if (action.type === CancelExternalTestFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  checkEmailExists$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CheckEmailExistsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.checkEmailExists(payload.email).pipe(
          map(response => {
            return CheckEmailExistsSuccess({
              email: payload.email,
              exists: response?.exists || response?.data?.exists || false,
              account: response?.account || null,
            });
          }),
          catchError(() => {
            return of(CheckEmailExistsFailure({ email: payload.email }));
          }),
          tap((action: any) => {
            if (action.type === CheckEmailExistsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === CheckEmailExistsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  checkPhoneExists$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CheckPhoneExistsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.checkPhoneExists(payload.phone).pipe(
          map(response => {
            return CheckPhoneExistsSuccess({
              phone: payload.phone,
              exists: response?.exists || response?.data?.exists || false,
              account: response?.account || null,
            });
          }),
          catchError(() => {
            return of(CheckPhoneExistsFailure({ phone: payload.phone }));
          }),
          tap((action: any) => {
            if (action.type === CheckPhoneExistsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === CheckPhoneExistsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  createDailyReportDraft$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateDailyReportDraftRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createDailyReport(payload).pipe(
          map(response => {
            return CreateDailyReportDraftSuccess({ data: response.data });
          }),
          catchError(() => {
            return of(CreateDailyReportDraftFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateDailyReportDraftSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === CreateDailyReportDraftFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  private datePipe: DatePipe;
  fetchChangeOrderFilterInSideNav$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchChangeOrderFilterInSideNavRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchChangeOrderFilterInSideNav(payload).pipe(
          map(response => {
            const { data } = response;
            const { change_orders } = data;
            const tempData = change_orders?.map(item => {
              const formatedDate = this.datePipe.transform(item.created_at, 'MMM d, y');
              return {
                ...item,
                key: item.id,
                value: item.order_number ? `${item.order_number} (${formatedDate})` : `${formatedDate}`,
              };
            });

            const sortedTempData = tempData.sort((a, b) => {
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            });
            return FetchChangeOrderFilterInSideNavSuccess({
              list: sortedTempData,
            });
          }),
          catchError(() => {
            return of(FetchChangeOrderFilterInSideNavFailure({}));
          }),
          tap((action: any) => {
            if (action.type === FetchChangeOrderFilterInSideNavSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchChangeOrderFilterInSideNavFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  // Billing Settings Effects
  fetchBillingSettings$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchBillingSettingsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchBillingSettings(payload).pipe(
          map(response => {
            return FetchBillingSettingsSuccess({
              billingSettings: response.data || {},
            });
          }),
          catchError((error) => {
            return of(FetchBillingSettingsFailure({ error }));
          }),
          tap((action: any) => {
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  updateBillingSettings$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateBillingSettingsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateBillingSettings(payload).pipe(
          map(response => {
            this.commonService.notification('Billing settings updated successfully', 'success');
            return UpdateBillingSettingsSuccess({
              billingSettings: response.data || {},
            });
          }),
          catchError((error) => {
            this.commonService.notification('Failed to update billing settings', 'error');
            return of(UpdateBillingSettingsFailure({ error }));
          }),
          tap((action: any) => {
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  // Project Calendar Effects
  fetchProjectCalendar$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchProjectCalendarRequest),
      mergeMap(action =>
        this.projectsService.fetchProjectCalendar(action.payload).pipe(
          map(response => {
            return FetchProjectCalendarSuccess({
              calendar: response.data || { project: {}, org: {} },
            });
          }),
          catchError((error) => {
            return of(FetchProjectCalendarFailure({ error }));
          }),
        ),
      ),
    ),
  );

  updateProjectCalendar$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateProjectCalendarRequest),
      tap(() => this.commonService.startLoading()),
      switchMap(action =>
        this.projectsService.updateProjectCalendar(action.payload).pipe(
          map(response => {
            this.commonService.notification('Calendar updated successfully', 'success');
            return UpdateProjectCalendarSuccess({
              calendar: response.data || { project: {}, org: {} },
            });
          }),
          catchError((error) => {
            this.commonService.notification('Failed to update calendar', 'error');
            return of(UpdateProjectCalendarFailure({ error }));
          }),
          tap(() => {
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  initializeProjectCalendar$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(InitializeProjectCalendarRequest),
      tap(() => this.commonService.startLoading()),
      switchMap(action =>
        this.projectsService.initializeProjectCalendar(action.payload).pipe(
          map(response => {
            this.commonService.notification('Calendar initialized from organization', 'success');
            return InitializeProjectCalendarSuccess({
              calendar: response.data || { project: {}, org: {} },
            });
          }),
          catchError((error) => {
            this.commonService.notification('Failed to initialize calendar', 'error');
            return of(InitializeProjectCalendarFailure({ error }));
          }),
          tap(() => {
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  fetchPayAppSummary$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchPayAppSummaryRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchPayAppSummary(payload).pipe(
          map(response => {
            return FetchPayAppSummarySuccess({
              payAppId: payload.payAppId,
              summary: response.data || {},
            });
          }),
          catchError((error) => {
            return of(FetchPayAppSummaryFailure({ error }));
          }),
          tap((action: any) => {
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  // Pay App Templates Effects
  fetchPayAppTemplates$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchPayAppTemplatesRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap(action => {
        // If both organizationId and projectId provided, fetch both and merge
        if (action.organizationId && action.projectId) {
          return forkJoin([
            this.projectsService.getPayAppTemplates(action.organizationId).pipe(catchError(() => of({ data: [] }))),
            this.projectsService.getProjectPayAppTemplates(action.projectId).pipe(catchError(() => of({ data: [] }))),
          ]).pipe(
            map(([orgResponse, projectResponse]) => {
              const orgTemplates = orgResponse.data || [];
              const projectTemplates = (projectResponse.data || []).map(pt => ({
                ...pt,
                project_id: action.projectId, // Add project_id so we can filter properly
              }));
              // Merge and dedupe by id
              const allTemplates = [...orgTemplates];
              projectTemplates.forEach(pt => {
                if (!allTemplates.find(t => t.id === pt.id)) {
                  allTemplates.push(pt);
                }
              });
              return FetchPayAppTemplatesSuccess({ templates: allTemplates });
            }),
            catchError((error) => of(FetchPayAppTemplatesFailure({ error }))),
            tap(() => this.commonService.stopLoading()),
          );
        }

        // Otherwise use single endpoint
        const apiCall = action.projectId
          ? this.projectsService.getProjectPayAppTemplates(action.projectId)
          : this.projectsService.getPayAppTemplates(action.organizationId);

        return apiCall.pipe(
          map(response => {
            return FetchPayAppTemplatesSuccess({
              templates: response.data || [],
            });
          }),
          catchError((error) => {
            return of(FetchPayAppTemplatesFailure({ error }));
          }),
          tap(() => {
            this.commonService.stopLoading();
          }),
        );
      }),
    ),
  );

  createPayAppTemplate$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreatePayAppTemplateRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap(action => {
        const apiCall = action.projectId
          ? this.projectsService.createProjectPayAppTemplate(action.projectId, action.template)
          : this.projectsService.createPayAppTemplate(action.organizationId, action.template);

        return apiCall.pipe(
          map(response => {
            this.commonService.notification('Template created successfully', 'success');
            return CreatePayAppTemplateSuccess({
              template: response.data,
            });
          }),
          catchError((error) => {
            this.commonService.notification('Failed to create template', 'error');
            return of(CreatePayAppTemplateFailure({ error }));
          }),
          tap(() => {
            this.commonService.stopLoading();
          }),
        );
      }),
    ),
  );

  updatePayAppTemplate$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdatePayAppTemplateRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap(action => {
        const apiCall = action.projectId
          ? this.projectsService.updateProjectPayAppTemplate(action.projectId, action.templateId, action.template)
          : this.projectsService.updatePayAppTemplate(action.organizationId, action.templateId, action.template);

        return apiCall.pipe(
          map(response => {
            this.commonService.notification('Template updated successfully', 'success');
            return UpdatePayAppTemplateSuccess({
              template: response.data,
            });
          }),
          catchError((error) => {
            this.commonService.notification('Failed to update template', 'error');
            return of(UpdatePayAppTemplateFailure({ error }));
          }),
          tap(() => {
            this.commonService.stopLoading();
          }),
        );
      }),
    ),
  );

  deletePayAppTemplate$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeletePayAppTemplateRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap(action =>
        this.projectsService.deletePayAppTemplate(action.organizationId, action.templateId).pipe(
          map(() => {
            this.commonService.notification('Template deleted successfully', 'success');
            return DeletePayAppTemplateSuccess({
              templateId: action.templateId,
            });
          }),
          catchError((error) => {
            this.commonService.notification('Failed to delete template', 'error');
            return of(DeletePayAppTemplateFailure({ error }));
          }),
          tap(() => {
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  previewPayAppTemplate$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(PreviewPayAppTemplateRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap(action =>
        this.projectsService.previewPayAppTemplate(action.templateId, action.payAppId).pipe(
          map(response => {
            // Backend returns { html: "..." } - open in new window
            const html = response.data?.html || response.html;
            if (html) {
              const previewWindow = window.open('', '_blank');
              if (previewWindow) {
                previewWindow.document.open();
                previewWindow.document.write(html);
                previewWindow.document.close();
              }
            }
            return PreviewPayAppTemplateSuccess({ previewUrl: null });
          }),
          catchError((error) => {
            this.commonService.notification('Failed to preview template', 'error');
            return of(PreviewPayAppTemplateFailure({ error }));
          }),
          tap(() => {
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  fetchAvailableCors$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchAvailableCorsRequest),
      map((action: any) => action.payload),
      mergeMap(payload =>
        this.projectsService.fetchAvailableCors(payload).pipe(
          map(response => {
            const { data } = response;
            // Calculate totalAmount for each COR
            data.forEach(cor => {
              let total = 0;
              if (cor?.lines?.length > 0) {
                cor.lines.forEach(line => {
                  total += line?.original_unit_price != null
                    ? (line.unit_price || 0) * ((line.original_quantity || 0) + (line.quantity || 0))
                      - (line.original_unit_price || 0) * (line.original_quantity || 0)
                    : (line.unit_price || 0) * (line.quantity || 0);
                });
              }
              cor.totalAmount = total;
            });
            return FetchAvailableCorsSuccess({ data });
          }),
          catchError(() => of(FetchAvailableCorsFailure())),
        )
      )
    )
  );

  createChangeOrderFromRequests$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateChangeOrderFromRequestsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createChangeOrderFromRequests(payload).pipe(
          map(response => {
            const { data } = response;
            return CreateChangeOrderFromRequestsSuccess({ data });
          }),
          catchError((error: any) => {
            // API returns validation messages as { status: 400, error: '<message>' }
            const message = typeof error?.error?.error === 'string'
              ? error.error.error
              : 'Failed to create change order.';
            this.dialog.open(AlertDialogComponent, {
              data: {
                title: 'Cannot create change order',
                message,
                okLabel: 'OK',
              },
              width: '400px',
            });
            return of(CreateChangeOrderFromRequestsFailure());
          }),
          tap((action: any) => {
            if (action.type === CreateChangeOrderFromRequestsSuccess.type) {
              this.commonService.notification('Change order created successfully.', 'success');
              // Re-fetch the CO list
              this.store.dispatch(
                FetchChangeOrdersRequestListRequest({
                  payload: {
                    projectId: payload.project_id,
                    qp: { start: 0, total: 10000 },
                  },
                })
              );
              this.projectsService.closeRightPanel.next();
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  linkRequestsToChangeOrder$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(LinkRequestsToChangeOrderRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.linkRequestsToChangeOrder(payload).pipe(
          map(response => {
            const { data } = response;
            return LinkRequestsToChangeOrderSuccess({ data });
          }),
          catchError((error: any) => {
            // API returns validation messages as { status: 400, error: '<message>' }
            const message = typeof error?.error?.error === 'string'
              ? error.error.error
              : 'Failed to link requests.';
            this.dialog.open(AlertDialogComponent, {
              data: {
                title: 'Cannot link requests',
                message,
                okLabel: 'OK',
              },
              width: '400px',
            });
            return of(LinkRequestsToChangeOrderFailure());
          }),
          tap((action: any) => {
            if (action.type === LinkRequestsToChangeOrderSuccess.type) {
              this.commonService.notification('Requests linked successfully.', 'success');
              this.projectsService.closeRightPanel.next();
              // Re-fetch CO data
              this.store.dispatch(
                FetchChangeOrdersRequestWithIdRequest({
                  payload: { changeOrdersRequestId: payload.changeOrderId }
                })
              );
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  unlinkRequestFromChangeOrder$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UnlinkRequestFromChangeOrderRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.unlinkRequestFromChangeOrder(payload).pipe(
          map(response => UnlinkRequestFromChangeOrderSuccess({ data: response?.data })),
          catchError((error: any) => {
            const message = typeof error?.error?.error === 'string'
              ? error.error.error
              : 'Failed to remove the request.';
            this.dialog.open(AlertDialogComponent, {
              data: { title: 'Cannot remove request', message, okLabel: 'OK' },
              width: '400px',
            });
            return of(UnlinkRequestFromChangeOrderFailure());
          }),
          tap((action: any) => {
            if (action.type === UnlinkRequestFromChangeOrderSuccess.type) {
              this.commonService.notification(`Request #${payload.requestNo} removed`, 'success');
              this.store.dispatch(
                FetchChangeOrdersRequestWithIdRequest({
                  payload: { changeOrdersRequestId: payload.changeOrderId }
                })
              );
              this.store.dispatch(FetchAvailableCorsRequest({ payload: { projectId: payload.projectId } }));
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchWarranties$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchWarrantiesRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchWarranties(payload).pipe(
          map(response => {
            this.commonService.stopLoading();
            return FetchWarrantiesSuccess({
              warrantiesInfo: {
                list: response.data || [],
                totalRecords: response.meta?.totalRecords || 0,
              },
            });
          }),
          catchError(() => {
            this.commonService.stopLoading();
            return of(FetchWarrantiesFailure());
          }),
        ),
      ),
    ),
  );

  createWarranty$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateWarrantyRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createWarranty(payload).pipe(
          map(response => {
            this.commonService.stopLoading();
            this.commonService.notification('Warranty created successfully', 'success');
            this.store.dispatch(FetchWarrantiesRequest({ payload: { projectId: payload.projectId } }));
            return CreateWarrantySuccess({ data: response.data });
          }),
          catchError(() => {
            this.commonService.stopLoading();
            this.commonService.notification('Failed to create warranty', 'error');
            return of(CreateWarrantyFailure());
          }),
        ),
      ),
    ),
  );

  updateWarranty$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateWarrantyRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.updateWarranty(payload).pipe(
          map(response => {
            this.commonService.stopLoading();
            this.commonService.notification('Warranty updated successfully', 'success');
            this.store.dispatch(FetchWarrantiesRequest({ payload: { projectId: payload.projectId } }));
            return UpdateWarrantySuccess({ data: response.data });
          }),
          catchError(() => {
            this.commonService.stopLoading();
            this.commonService.notification('Failed to update warranty', 'error');
            return of(UpdateWarrantyFailure());
          }),
        ),
      ),
    ),
  );

  deleteWarranty$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteWarrantyRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.deleteWarranty(payload).pipe(
          map(() => {
            this.commonService.stopLoading();
            this.commonService.notification('Warranty deleted', 'success');
            this.store.dispatch(FetchWarrantiesRequest({ payload: { projectId: payload.projectId } }));
            return DeleteWarrantySuccess({});
          }),
          catchError(() => {
            this.commonService.stopLoading();
            return of(DeleteWarrantyFailure());
          }),
        ),
      ),
    ),
  );

  fetchWarrantyClaims$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchWarrantyClaimsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchWarrantyClaims(payload).pipe(
          map(response => {
            this.commonService.stopLoading();
            return FetchWarrantyClaimsSuccess({
              warrantyClaimsInfo: {
                list: response.data || [],
                totalRecords: response.meta?.totalRecords || 0,
              },
            });
          }),
          catchError(() => {
            this.commonService.stopLoading();
            return of(FetchWarrantyClaimsFailure());
          }),
        ),
      ),
    ),
  );

  createWarrantyClaim$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateWarrantyClaimRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.createWarrantyClaim(payload).pipe(
          map(response => {
            void this.sheetMarkups.flush(response);
            this.commonService.stopLoading();
            this.commonService.notification('Warranty claim filed successfully', 'success');
            this.store.dispatch(FetchWarrantyClaimsRequest({ payload: { projectId: payload.projectId, warrantyId: payload.warrantyId } }));
            return CreateWarrantyClaimSuccess({ data: response.data });
          }),
          catchError(err => {
            this.commonService.stopLoading();
            this.commonService.notification(this.getApiErrorMessage(err, 'Failed to file warranty claim'), 'error');
            return of(CreateWarrantyClaimFailure());
          }),
        ),
      ),
    ),
  );

  fetchWarrantyClaimDetail$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchWarrantyClaimDetailRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.fetchWarrantyClaimDetail(payload).pipe(
          map(response => {
            this.commonService.stopLoading();
            return FetchWarrantyClaimDetailSuccess({ warrantyClaimDetail: response.data });
          }),
          catchError(() => {
            this.commonService.stopLoading();
            return of(FetchWarrantyClaimDetailFailure());
          }),
        ),
      ),
    ),
  );

  warrantyClaimAction$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(WarrantyClaimActionRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.projectsService.warrantyClaimAction(payload).pipe(
          map(response => {
            this.commonService.stopLoading();
            this.commonService.notification(`Claim ${payload.action} successful`, 'success');
            if (payload.claimId) {
              this.store.dispatch(FetchWarrantyClaimDetailRequest({ payload: { claimId: payload.claimId } }));
            }
            if (payload.projectId && payload.warrantyId) {
              this.store.dispatch(FetchWarrantyClaimsRequest({ payload: { projectId: payload.projectId, warrantyId: payload.warrantyId } }));
            }
            return WarrantyClaimActionSuccess({ data: response.data });
          }),
          catchError(() => {
            this.commonService.stopLoading();
            this.commonService.notification('Claim action failed', 'error');
            return of(WarrantyClaimActionFailure());
          }),
        ),
      ),
    ),
  );

  /** Picker strokes held for a sheet link that only exists once its record saves (drawings slice 3). */
  private sheetMarkups = inject(SheetMarkupQueueService);

  constructor(
    private commonService: CommonService,
    private store: Store<fromRoot.State>,
    private projectsService: ProjectsService,
    private searchService: SearchService,
    private issueService: IssueService,
    private workflowService: WorkflowService, // Add this
    private actions: Actions,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private injector: Injector,
    private dailyReportService: DailyReportService,
    public dialog: MatDialog,
    private analyticsService: AnalyticsService,
    private siteMaterialTestService: SiteMaterialTestService,
  ) {
    this.datePipe = this.injector.get(DatePipe);

    this.route.queryParams
      .subscribe(params => {
        if (params && params.fornType) {
          this.formType = params.fornType;
        }
      })
      .unsubscribe();
  }

  private getApiErrorMessage(err: any, fallback: string): string {
    const error = typeof err === 'function' ? err() : err;
    const errorBody = error?.error;
    if (typeof errorBody?.error === 'string') {
      return errorBody.error;
    }
    if (typeof errorBody?.error === 'object') {
      const messages = Object.values(errorBody.error)
        .map((field: any) => field?.msg)
        .filter(Boolean);
      if (messages.length) {
        return messages.join(', ');
      }
    }
    return errorBody?.message || fallback;
  }

  /**
   * 409 report_changed on a daily report save. Refresh re-fetches the report (closing the
   * open editor, so the form re-renders from server data); Cancel leaves everything as is, and
   * the next save 409s again until the user refreshes.
   */
  private openDailyReportChangedDialog(conflict: ReportChangedBody): void {
    openReportChangedDialog(this.dialog, conflict, this.authService.getCurrentUser(), 'report')
      .subscribe(refresh => {
        if (!refresh) return;
        this.projectsService.isAnyFormDirty.next(false);
        this.dailyReportService.fetchDailyReportById.next({});
      });
  }

  private getDailyReportSaveErrorMessage(err: any): string {
    const errorMessage = this.getApiErrorMessage(err, 'Failed to save report.');
    if (errorMessage.includes('Cannot execute') && errorMessage.includes('DailyReport')) {
      return 'Only the report creator or an upstream reporting manager can edit this daily report.';
    }
    return errorMessage;
  }

  calculateRollupQuantity(fields, fieldValues, rollupFormula) {
    const fieldValuesMap = {};

    fields.forEach(field => {
      const fieldValue = fieldValues?.find(fv => fv?.id === field?.id);
      if (fieldValue && fieldValue.value !== null && fieldValue.value !== undefined) {
        fieldValuesMap[field.name] = fieldValue.value;
      }
    });

    const hasValidFields = fieldValuesMap.hasOwnProperty('length_f') && fieldValuesMap.hasOwnProperty('width_f');

    if (hasValidFields) {
      const evaluatedQuantity = Parser.evaluate(rollupFormula, fieldValuesMap);
      return !isNaN(evaluatedQuantity) ? evaluatedQuantity.toFixed(2) : 0;
    }

    return 0;
  }

  segregateLinesBySection(fields, lines) {
    const sectionMap = {};

    lines.forEach(line => {
      if (line.field_values?.length > 0) {
        const fieldId = line.field_values[0].id;
        const field = fields?.find(f => f.id === fieldId);

        if (field) {
          const section = field.ui_config?.['section_name'] || 'general';

          if (!sectionMap[section]) {
            sectionMap[section] = [];
          }

          sectionMap[section].push(line);
        }
      }
    });

    return sectionMap;
  }

  // SCAN BATCH TICKET
  scanBatchTicket$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ScanBatchTicketRequest),
      map((action: any) => action.payload),
      mergeMap(payload =>
        this.projectsService.scanBatchTicket(payload).pipe(
          map(response => {
            const data = response?.data || response;
            this.commonService.notification('Batch ticket scanned successfully', 'success');
            return ScanBatchTicketSuccess({ data });
          }),
          catchError(() => {
            this.commonService.notification('Failed to scan batch ticket', 'danger');
            return of(ScanBatchTicketFailure());
          }),
        )
      )
    )
  );
}
